import React, { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { useAuth, useLanguage, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import LogoUploader from "../components/LogoUploader";
import TenantManageDialog from "../components/TenantManageDialog";
import SalesRankingDashboard from "../components/SalesRankingDashboard";
import DemoFunnelDashboard from "../components/DemoFunnelDashboard";
import AdminPipelineDashboard from "../components/AdminPipelineDashboard";
import SuperAdminWhatsAppTab from "../components/SuperAdminWhatsAppTab";
import LandingAnalyticsDashboard from "../components/LandingAnalyticsDashboard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Plus, 
  Building2, 
  Users, 
  CreditCard,
  TrendingUp,
  Check,
  X,
  ExternalLink,
  Palette,
  Ban,
  Trash2,
  CheckCircle,
  Calendar,
  AlertTriangle,
  BarChart3,
  Euro,
  Gift,
  Power,
  Eye,
  Settings,
  Clock,
  MapPin,
  Sparkles,
  Store,
  ChevronDown,
  ChevronUp,
  Phone,
  MessageSquare,
  Filter,
  FileText,
  CircleDot,
  Loader2,
  Search,
  Shield,
  Download,
  Trophy,
  Award,
  Rocket,
  Mail,
  PhoneCall,
  StickyNote,
  Instagram,
  Bell,
  User as UserIcon,
  History,
  Briefcase,
  Crown,
  Tag,
  Wallet,
  KeyRound
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

const SuperAdmin = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [newLeadsCount, setNewLeadsCount] = useState(0);

  useEffect(() => {
    const fetchNewLeads = async () => {
      try {
        const res = await axios.get(`${API}/leads/list`, { withCredentials: true });
        setNewLeadsCount(res.data.stats?.new || 0);
      } catch {}
    };
    fetchNewLeads();
    const interval = setInterval(fetchNewLeads, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-serif font-medium">{t("super_admin")}</h1>
            <p className="text-muted-foreground mt-1">
              {language === "it" ? "Gestione piattaforma SaaS" : "SaaS Platform Management"}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b pb-2 flex-wrap">
          <Link to="/admin">
            <Button 
              variant={location.pathname === "/admin" ? "default" : "ghost"}
              className="rounded-full"
            >
              <Building2 className="w-4 h-4 mr-2" />
              {t("tenants")}
            </Button>
          </Link>
          <Link to="/admin/subscriptions">
            <Button 
              variant={location.pathname === "/admin/subscriptions" ? "default" : "ghost"}
              className="rounded-full"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {t("subscriptions")}
            </Button>
          </Link>
          <Link to="/admin/analytics">
            <Button 
              variant={location.pathname === "/admin/analytics" ? "default" : "ghost"}
              className="rounded-full"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </Button>
          </Link>
          <Link to="/admin/leads">
            <Button 
              variant={location.pathname === "/admin/leads" ? "default" : "ghost"}
              className="rounded-full relative"
              data-testid="leads-tab"
            >
              <FileText className="w-4 h-4 mr-2" />
              Leads
              {newLeadsCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold px-1.5 shadow-lg shadow-red-500/30 animate-in fade-in"
                  data-testid="new-leads-badge"
                >
                  {newLeadsCount > 99 ? "99+" : newLeadsCount}
                </span>
              )}
            </Button>
          </Link>
          <Link to="/admin/sales">
            <Button 
              variant={location.pathname === "/admin/sales" ? "default" : "ghost"}
              className="rounded-full"
              data-testid="sales-management-tab"
            >
              <Users className="w-4 h-4 mr-2" />
              Sales
            </Button>
          </Link>
          <Link to="/admin/pipeline">
            <Button
              variant={location.pathname === "/admin/pipeline" ? "default" : "ghost"}
              className="rounded-full"
              data-testid="pipeline-tab"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Pipeline
            </Button>
          </Link>
          <Link to="/admin/ranking">
            <Button
              variant={location.pathname === "/admin/ranking" ? "default" : "ghost"}
              className="rounded-full"
              data-testid="sales-ranking-tab"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Ranking
            </Button>
          </Link>
          <Link to="/admin/demo-funnel">
            <Button
              variant={location.pathname === "/admin/demo-funnel" ? "default" : "ghost"}
              className="rounded-full"
              data-testid="demo-funnel-tab"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Demo Funnel
            </Button>
          </Link>
          <Link to="/admin/landing-analytics">
            <Button
              variant={location.pathname === "/admin/landing-analytics" ? "default" : "ghost"}
              className="rounded-full"
              data-testid="landing-analytics-tab"
            >
              <Rocket className="w-4 h-4 mr-2" />
              Landing
            </Button>
          </Link>
          <Link to="/admin/gdpr">
            <Button 
              variant={location.pathname === "/admin/gdpr" ? "default" : "ghost"}
              className="rounded-full"
              data-testid="gdpr-tab"
            >
              <Shield className="w-4 h-4 mr-2" />
              GDPR
            </Button>
          </Link>
          <Link to="/admin/whatsapp">
            <Button
              variant={location.pathname === "/admin/whatsapp" ? "default" : "ghost"}
              className="rounded-full"
              data-testid="whatsapp-tab"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </Link>
        </div>

        <Routes>
          <Route index element={<TenantsManagement />} />
          <Route path="subscriptions" element={<SubscriptionsManagement />} />
          <Route path="analytics" element={<SuperAdminAnalytics />} />
          <Route path="leads" element={<LeadsManagement onLeadUpdate={() => {
            axios.get(`${API}/leads/list`, { withCredentials: true }).then(res => setNewLeadsCount(res.data.stats?.new || 0)).catch(() => {});
          }} />} />
          <Route path="sales" element={<SalesManagement />} />
          <Route path="pipeline" element={<AdminPipelineDashboard />} />
          <Route path="ranking" element={<SalesRankingDashboard />} />
          <Route path="demo-funnel" element={<DemoFunnelDashboard />} />
          <Route path="landing-analytics" element={<LandingAnalyticsDashboard />} />
          <Route path="gdpr" element={<GDPRDashboard />} />
          <Route path="whatsapp" element={<SuperAdminWhatsAppTab />} />
        </Routes>
      </div>
    </AdminLayout>
  );
};

const TenantsManagement = () => {
  const { t, language } = useLanguage();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [manageTenant, setManageTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    owner_email: "",
    owner_name: "",
    owner_password: "",
    phone: "",
    address: "",
    logo_url: "",
    max_concurrent_bookings: 1,
    plan_type: "mono_sede", // "mono_sede" or "multi_sede"
    billing_period: "monthly", // "monthly" or "annual"
    branding: {
      primary_color: "#6366f1",
      secondary_color: "#8b5cf6",
      accent_color: "#ec4899"
    },
    // Multi-sede locations
    locations: []
  });
  const [showLocationsSection, setShowLocationsSection] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: "",
    slug: "",
    address: "",
    phone: "",
    logo_url: "",
    branding: {
      primary_color: "#6366f1",
      secondary_color: "#8b5cf6",
      accent_color: "#ec4899"
    }
  });
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [billingForm, setBillingForm] = useState({
    subscription_tier: "essential",
    custom_enabled:    false,
    plan_label:        "",
    monthly_price:     "",
    annual_price:      "",
    billing_cycle:     "monthly",
    notes:             "",
  });
  const [billingSaving, setBillingSaving] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedTenantAnalytics, setSelectedTenantAnalytics] = useState(null);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [passwordResetUsers, setPasswordResetUsers] = useState([]);
  const [passwordResetTargetUserId, setPasswordResetTargetUserId] = useState("");
  const [passwordResetValue, setPasswordResetValue] = useState("");
  const [passwordResetSubmitting, setPasswordResetSubmitting] = useState(false);
  const [planRepairOpen, setPlanRepairOpen] = useState(false);
  const [planRepairPlans, setPlanRepairPlans] = useState([]);
  const [planRepairTarget, setPlanRepairTarget] = useState("");
  const [planRepairSubmitting, setPlanRepairSubmitting] = useState(false);
  const [blockReason, setBlockReason] = useState("");

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/tenants`, { withCredentials: true });
      setTenants(response.data);
    } catch (error) {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/tenants`, formData, { withCredentials: true });
      toast.success(language === "it" ? "Attività creata!" : "Business created!");
      setDialogOpen(false);
      setFormData({ 
        name: "", 
        slug: "", 
        owner_email: "", 
        owner_name: "",
        owner_password: "",
        phone: "", 
        address: "",
        logo_url: "",
        max_concurrent_bookings: 1,
        plan_type: "mono_sede",
        billing_period: "monthly",
        branding: {
          primary_color: "#6366f1",
          secondary_color: "#8b5cf6",
          accent_color: "#ec4899"
        },
        locations: []
      });
      setShowLocationsSection(false);
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const getStatusBadge = (status, isBlocked) => {
    if (isBlocked) return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
    const classes = {
      active: "badge-success",
      trial: "badge-warning",
      cancelled: "badge-error",
      expired: "badge-error",
      suspended: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
    };
    return classes[status] || "bg-muted";
  };

  const handleBlockTenant = async () => {
    if (!selectedTenant) return;
    
    try {
      await axios.post(`${API}/tenants/${selectedTenant.tenant_id}/block`, {
        is_blocked: !selectedTenant.is_blocked,
        reason: blockReason || (language === "it" ? "Bloccato dall'amministratore" : "Blocked by administrator")
      }, { withCredentials: true });
      
      toast.success(
        selectedTenant.is_blocked 
          ? (language === "it" ? "Attività sbloccata!" : "Business unblocked!")
          : (language === "it" ? "Attività bloccata!" : "Business blocked!")
      );
      setBlockDialogOpen(false);
      setBlockReason("");
      setSelectedTenant(null);
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenant) return;
    
    try {
      await axios.delete(`${API}/tenants/${selectedTenant.tenant_id}`, { withCredentials: true });
      toast.success(language === "it" ? "Attività eliminata!" : "Business deleted!");
      setDeleteDialogOpen(false);
      setSelectedTenant(null);
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const handleToggleFreeForever = async (tenant) => {
    try {
      const isFree = tenant.free_forever || tenant.is_free;
      if (isFree) {
        // Remove free status
        await axios.post(`${API}/superadmin/tenants/${tenant.tenant_id}/remove-free`, {}, { withCredentials: true });
        toast.success(language === "it" ? "Abbonamento gratuito rimosso!" : "Free subscription removed!");
      } else {
        // Set as free
        await axios.post(`${API}/superadmin/tenants/${tenant.tenant_id}/set-free`, {}, { withCredentials: true });
        toast.success(language === "it" ? "Abbonamento impostato come gratuito!" : "Subscription set as free!");
      }
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const handleExtendTrial = async (tenant) => {
    try {
      await axios.post(`${API}/subscription/admin/extend-trial`, {
        tenant_id: tenant.tenant_id,
        days: 14
      }, { withCredentials: true });
      toast.success(language === "it" ? "Trial esteso di 14 giorni!" : "Trial extended by 14 days!");
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const openBillingDialog = (tenant) => {
    setSelectedTenant(tenant);
    const cp = tenant.custom_pricing || {};
    setBillingForm({
      subscription_tier: tenant.subscription_tier || "essential",
      custom_enabled:    !!cp.enabled,
      plan_label:        cp.plan_label || "",
      monthly_price:     cp.monthly_price != null ? String(cp.monthly_price) : "",
      annual_price:      cp.annual_price  != null ? String(cp.annual_price)  : "",
      billing_cycle:     cp.billing_cycle || "monthly",
      notes:             cp.notes || "",
    });
    setBillingDialogOpen(true);
  };

  // ── PASSWORD RESET (SuperAdmin) ─────────────────────────────────────
  const openPasswordResetDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setPasswordResetTargetUserId("");
    setPasswordResetValue("");
    setPasswordResetUsers([]);
    setPasswordResetOpen(true);
    try {
      const r = await axios.get(
        `${API}/superadmin/tenants/${tenant.tenant_id}/users`,
        { withCredentials: true }
      );
      const users = r.data?.users || [];
      setPasswordResetUsers(users);
      const admin = users.find(u => u.role === "salon_admin") || users[0];
      if (admin) setPasswordResetTargetUserId(admin.user_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore caricamento utenti" : "Failed to load users"));
    }
  };

  const submitPasswordReset = async () => {
    if (!selectedTenant || !passwordResetTargetUserId) return;
    if (passwordResetValue.length < 8) {
      toast.error(language === "it" ? "Min 8 caratteri" : "Min 8 chars");
      return;
    }
    setPasswordResetSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/superadmin/tenants/${selectedTenant.tenant_id}/users/${passwordResetTargetUserId}/reset-password`,
        { new_password: passwordResetValue },
        { withCredentials: true }
      );
      toast.success(language === "it"
        ? `Password aggiornata per ${r.data?.email || "utente"}`
        : `Password updated for ${r.data?.email || "user"}`);
      setPasswordResetOpen(false);
      setPasswordResetValue("");
      setPasswordResetTargetUserId("");
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore reset password" : "Reset failed"));
    } finally {
      setPasswordResetSubmitting(false);
    }
  };

  // ── PLAN REPAIR (SuperAdmin) ───────────────────────────────────
  const openPlanRepairDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setPlanRepairTarget("");
    setPlanRepairOpen(true);
    try {
      const r = await axios.get(`${API}/superadmin/plans`, { withCredentials: true });
      const plans = r.data?.plans || [];
      setPlanRepairPlans(plans);
      // Pre-select best guess: vertical match + tier=pro > growth > starter, billing=monthly
      const vertical = (tenant.vertical || "").toLowerCase();
      const preferOrder = ["pro_monthly", "growth_monthly", "starter_monthly", "pro_yearly", "growth_yearly", "starter_yearly"];
      let best = null;
      for (const suffix of preferOrder) {
        best = plans.find(p => p.plan_id === `${vertical}_${suffix}`);
        if (best) break;
      }
      if (best) setPlanRepairTarget(best.plan_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore caricamento piani" : "Failed to load plans"));
    }
  };

  const submitPlanRepair = async () => {
    if (!selectedTenant || !planRepairTarget) return;
    setPlanRepairSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/superadmin/tenants/${selectedTenant.tenant_id}/repair-plan`,
        { plan_id: planRepairTarget },
        { withCredentials: true }
      );
      toast.success(language === "it"
        ? `Piano riallineato: ${r.data?.features_count} feature attive`
        : `Plan synced: ${r.data?.features_count} features enabled`);
      setPlanRepairOpen(false);
      setPlanRepairTarget("");
      // Refresh tenants list to reflect new vertical/tier
      if (typeof fetchTenants === "function") fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore riparazione piano" : "Repair failed"));
    } finally {
      setPlanRepairSubmitting(false);
    }
  };

  // Tier presets (sync with backend)
  const TIER_PRESETS = {
    essential:     { label: "Essential",         monthly: 34.95, annual: 349.00 },
    growth:        { label: "Growth",            monthly: 59.00, annual: 590.00 },
    pro:           { label: "Pro",               monthly: 89.00, annual: 890.00 },
    dental_pro:    { label: "SmartBooker Dental Pro", monthly: 179.00, annual: 1790.00 },
    benessere_pro: { label: "SmartBooker Benessere Pro", monthly: 89.00, annual: 890.00 },
    custom:        { label: "Custom",            monthly: 0,     annual: 0 },
  };

  const handleBillingTierChange = (tier) => {
    const preset = TIER_PRESETS[tier];
    setBillingForm(f => ({
      ...f,
      subscription_tier: tier,
      // If custom flag is OFF, suggest preset values; if ON, keep current values
      plan_label:    f.custom_enabled ? f.plan_label    : (preset?.label    || ""),
      monthly_price: f.custom_enabled ? f.monthly_price : (preset ? String(preset.monthly) : ""),
      annual_price:  f.custom_enabled ? f.annual_price  : (preset ? String(preset.annual)  : ""),
    }));
  };

  const handleSaveBilling = async () => {
    if (!selectedTenant) return;
    setBillingSaving(true);
    try {
      const payload = { subscription_tier: billingForm.subscription_tier };
      if (billingForm.custom_enabled) {
        payload.custom_pricing = {
          enabled:       true,
          plan_label:    billingForm.plan_label || null,
          monthly_price: Number(billingForm.monthly_price || 0),
          annual_price:  Number(billingForm.annual_price  || 0),
          billing_cycle: billingForm.billing_cycle,
          currency:      "EUR",
          notes:         billingForm.notes || null,
        };
      } else {
        payload.custom_pricing = { enabled: false };
      }
      await axios.patch(`${API}/superadmin/tenants/${selectedTenant.tenant_id}/billing-profile`,
        payload, { withCredentials: true });
      toast.success(language === "it" ? "Profilo billing aggiornato" : "Billing profile updated");
      setBillingDialogOpen(false);
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore aggiornamento" : "Update failed"));
    } finally { setBillingSaving(false); }
  };

  const fetchTenantAnalytics = async (tenant) => {
    try {
      const response = await axios.get(`${API}/analytics/tenant/${tenant.tenant_id}`, { 
        withCredentials: true 
      });
      setSelectedTenantAnalytics(response.data);
    } catch (error) {
      toast.error(language === "it" ? "Errore nel caricamento analytics" : "Error loading analytics");
      setSelectedTenantAnalytics(null);
    }
  };

  // Fetch analytics when dialog opens
  useEffect(() => {
    if (analyticsDialogOpen && selectedTenant) {
      fetchTenantAnalytics(selectedTenant);
    }
  }, [analyticsDialogOpen, selectedTenant]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy");
    } catch {
      return "-";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full btn-lift" data-testid="create-tenant-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t("create_salon")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("create_salon")}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {language === "it" ? "Nome Attività" : "Business Name"} *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Hair Studio Milano"
                  required
                  data-testid="tenant-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">Slug (URL) *</label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") 
                  })}
                  placeholder="hair-studio-milano"
                  required
                  data-testid="tenant-slug-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {language === "it" ? "Sarà usato per:" : "Will be used for:"} /book/{formData.slug || "your-slug"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    {language === "it" ? "Nome Proprietario" : "Owner Name"} *
                  </label>
                  <Input
                    value={formData.owner_name}
                    onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                    required
                    data-testid="tenant-owner-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    {language === "it" ? "Email Proprietario" : "Owner Email"} *
                  </label>
                  <Input
                    type="email"
                    value={formData.owner_email}
                    onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                    required
                    data-testid="tenant-owner-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {language === "it" ? "Password Proprietario" : "Owner Password"} *
                </label>
                <Input
                  type="password"
                  value={formData.owner_password}
                  onChange={(e) => setFormData({ ...formData, owner_password: e.target.value })}
                  required
                  placeholder={language === "it" ? "Minimo 6 caratteri" : "Minimum 6 characters"}
                  data-testid="tenant-owner-password-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {language === "it" 
                    ? "Il proprietario userà questa password per accedere al pannello gestione" 
                    : "The owner will use this password to access the management panel"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">{t("phone")}</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    {language === "it" ? "Indirizzo" : "Address"}
                  </label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
              </div>

              {/* Concurrent Bookings / Stations */}
              <div className="p-4 rounded-lg border bg-muted/30">
                <label className="block text-sm font-medium mb-1.5">
                  {language === "it" ? "Numero Postazioni / Poltrone" : "Number of Stations / Chairs"}
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  {language === "it" 
                    ? "Quanti appuntamenti possono essere presi contemporaneamente nello stesso slot orario" 
                    : "How many appointments can be booked at the same time slot"}
                </p>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={formData.max_concurrent_bookings}
                  onChange={(e) => setFormData({ ...formData, max_concurrent_bookings: parseInt(e.target.value) || 1 })}
                  className="w-24"
                  data-testid="tenant-stations-input"
                />
              </div>

              {/* Subscription Plan */}
              <div className="space-y-3">
                <label className="block text-sm font-medium">
                  {language === "it" ? "Tipo di Piano" : "Plan Type"} *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, plan_type: "mono_sede", locations: [] });
                      setShowLocationsSection(false);
                    }}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      formData.plan_type === "mono_sede" 
                        ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20" 
                        : "border-border hover:border-violet-300"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4 text-violet-500" />
                      <span className="font-medium text-sm">
                        {language === "it" ? "Mono Sede" : "Single Location"}
                      </span>
                    </div>
                    <p className="text-lg font-bold">€34,95<span className="text-xs font-normal text-muted-foreground">/mese</span></p>
                    <p className="text-xs text-muted-foreground">14 giorni trial</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, plan_type: "multi_sede" });
                      setShowLocationsSection(true);
                    }}
                    className={`p-3 rounded-xl border-2 text-left transition-all relative ${
                      formData.plan_type === "multi_sede" 
                        ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20" 
                        : "border-border hover:border-violet-300"
                    }`}
                  >
                    <div className="absolute -top-2 -right-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 text-white text-xs font-medium">
                        <Sparkles className="w-3 h-3" />
                        Business
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="w-4 h-4 text-purple-500" />
                      <span className="font-medium text-sm">
                        {language === "it" ? "Multi Sede" : "Multi Location"}
                      </span>
                    </div>
                    <p className="text-lg font-bold">€59,00<span className="text-xs font-normal text-muted-foreground">/mese</span></p>
                    <p className="text-xs text-muted-foreground">30 giorni trial • Max 10 sedi</p>
                  </button>
                </div>
              </div>

              {/* Billing Period */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {language === "it" ? "Periodo Fatturazione" : "Billing Period"}
                </label>
                <Select
                  value={formData.billing_period}
                  onValueChange={(value) => setFormData({ ...formData, billing_period: value })}
                >
                  <SelectTrigger data-testid="tenant-billing-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">
                      {language === "it" ? "Mensile" : "Monthly"}
                    </SelectItem>
                    <SelectItem value="annual">
                      {language === "it" ? "Annuale (2 mesi gratis)" : "Annual (2 months free)"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Multi-sede Locations Section */}
              {formData.plan_type === "multi_sede" && (
                <div className="space-y-4 p-4 rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/50 dark:bg-violet-900/10">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <Store className="w-4 h-4 text-violet-500" />
                      {language === "it" ? "Gestione Sedi" : "Location Management"}
                    </h4>
                    <span className="text-xs text-muted-foreground">
                      {formData.locations.length}/10 {language === "it" ? "sedi" : "locations"}
                    </span>
                  </div>

                  {/* Existing Locations */}
                  {formData.locations.length > 0 && (
                    <div className="space-y-2">
                      {formData.locations.map((loc, idx) => (
                        <div key={idx} className="p-3 bg-white dark:bg-slate-800 rounded-lg border flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {loc.logo_url ? (
                              <img src={loc.logo_url} alt={loc.name} className="w-8 h-8 rounded object-cover" />
                            ) : (
                              <div 
                                className="w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold"
                                style={{ backgroundColor: loc.branding?.primary_color || "#6366f1" }}
                              >
                                {loc.name.charAt(0)}
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-sm">{loc.name}</p>
                              <p className="text-xs text-muted-foreground">/book/{loc.slug}</p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                locations: formData.locations.filter((_, i) => i !== idx)
                              });
                            }}
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Location Form */}
                  {formData.locations.length < 10 && (
                    <div className="space-y-3 p-3 bg-white dark:bg-slate-800 rounded-lg border">
                      <p className="text-sm font-medium">
                        {language === "it" ? "Aggiungi Sede" : "Add Location"}
                      </p>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder={language === "it" ? "Nome sede" : "Location name"}
                          value={newLocation.name}
                          onChange={(e) => setNewLocation({ 
                            ...newLocation, 
                            name: e.target.value,
                            slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-")
                          })}
                          className="text-sm"
                        />
                        <Input
                          placeholder="slug-sede"
                          value={newLocation.slug}
                          onChange={(e) => setNewLocation({ 
                            ...newLocation, 
                            slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") 
                          })}
                          className="text-sm"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder={language === "it" ? "Indirizzo" : "Address"}
                          value={newLocation.address}
                          onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                          className="text-sm"
                        />
                        <Input
                          placeholder={language === "it" ? "Telefono" : "Phone"}
                          value={newLocation.phone}
                          onChange={(e) => setNewLocation({ ...newLocation, phone: e.target.value })}
                          className="text-sm"
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <input
                            type="color"
                            value={newLocation.branding.primary_color}
                            onChange={(e) => setNewLocation({ 
                              ...newLocation, 
                              branding: { ...newLocation.branding, primary_color: e.target.value }
                            })}
                            className="w-8 h-8 rounded cursor-pointer border"
                            title={language === "it" ? "Colore primario" : "Primary color"}
                          />
                          <input
                            type="color"
                            value={newLocation.branding.secondary_color}
                            onChange={(e) => setNewLocation({ 
                              ...newLocation, 
                              branding: { ...newLocation.branding, secondary_color: e.target.value }
                            })}
                            className="w-8 h-8 rounded cursor-pointer border"
                            title={language === "it" ? "Colore secondario" : "Secondary color"}
                          />
                        </div>
                        <Input
                          placeholder={language === "it" ? "URL Logo (opzionale)" : "Logo URL (optional)"}
                          value={newLocation.logo_url}
                          onChange={(e) => setNewLocation({ ...newLocation, logo_url: e.target.value })}
                          className="text-sm flex-1"
                        />
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={!newLocation.name || !newLocation.slug}
                        onClick={() => {
                          if (newLocation.name && newLocation.slug) {
                            setFormData({
                              ...formData,
                              locations: [...formData.locations, { ...newLocation, location_id: `loc_${Date.now()}` }]
                            });
                            setNewLocation({
                              name: "",
                              slug: "",
                              address: "",
                              phone: "",
                              logo_url: "",
                              branding: {
                                primary_color: "#6366f1",
                                secondary_color: "#8b5cf6",
                                accent_color: "#ec4899"
                              }
                            });
                          }
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {language === "it" ? "Aggiungi Sede" : "Add Location"}
                      </Button>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    {language === "it" 
                      ? "Ogni sede avrà la propria pagina white-label, servizi e prezzi personalizzabili."
                      : "Each location will have its own white-label page, customizable services and prices."
                    }
                  </p>
                </div>
              )}

              {/* White Label Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  {language === "it" ? "White Label / Branding" : "White Label / Branding"}
                </h4>
                
                <div className="space-y-4">
                  <LogoUploader
                    value={formData.logo_url}
                    onChange={(url) => setFormData({ ...formData, logo_url: url })}
                    language={language}
                  />

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">
                        {language === "it" ? "Colore Primario" : "Primary Color"}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.branding.primary_color}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            branding: { ...formData.branding, primary_color: e.target.value }
                          })}
                          className="w-10 h-10 rounded cursor-pointer border"
                          data-testid="tenant-primary-color"
                        />
                        <Input
                          value={formData.branding.primary_color}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            branding: { ...formData.branding, primary_color: e.target.value }
                          })}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1.5">
                        {language === "it" ? "Colore Secondario" : "Secondary Color"}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.branding.secondary_color}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            branding: { ...formData.branding, secondary_color: e.target.value }
                          })}
                          className="w-10 h-10 rounded cursor-pointer border"
                          data-testid="tenant-secondary-color"
                        />
                        <Input
                          value={formData.branding.secondary_color}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            branding: { ...formData.branding, secondary_color: e.target.value }
                          })}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1.5">
                        {language === "it" ? "Colore Accento" : "Accent Color"}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={formData.branding.accent_color}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            branding: { ...formData.branding, accent_color: e.target.value }
                          })}
                          className="w-10 h-10 rounded cursor-pointer border"
                          data-testid="tenant-accent-color"
                        />
                        <Input
                          value={formData.branding.accent_color}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            branding: { ...formData.branding, accent_color: e.target.value }
                          })}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="p-4 rounded-lg border bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-2">
                      {language === "it" ? "Anteprima:" : "Preview:"}
                    </p>
                    <div className="flex items-center gap-3">
                      {formData.logo_url ? (
                        <img 
                          src={formData.logo_url} 
                          alt="Logo preview" 
                          className="h-8 w-auto"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      ) : (
                        <div 
                          className="w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold"
                          style={{ backgroundColor: formData.branding.primary_color }}
                        >
                          {formData.name?.charAt(0) || "S"}
                        </div>
                      )}
                      <span className="font-semibold">{formData.name || "Nome Attività"}</span>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <div 
                        className="px-4 py-2 rounded text-white text-sm"
                        style={{ backgroundColor: formData.branding.primary_color }}
                      >
                        {language === "it" ? "Pulsante Primario" : "Primary Button"}
                      </div>
                      <div 
                        className="px-4 py-2 rounded text-white text-sm"
                        style={{ backgroundColor: formData.branding.secondary_color }}
                      >
                        {language === "it" ? "Secondario" : "Secondary"}
                      </div>
                      <div 
                        className="px-4 py-2 rounded text-white text-sm"
                        style={{ backgroundColor: formData.branding.accent_color }}
                      >
                        {language === "it" ? "Accento" : "Accent"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t("cancel")}
                </Button>
                <Button type="submit" data-testid="save-tenant-btn">
                  {t("save")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tenants Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tenants.map((tenant) => (
          <div 
            key={tenant.tenant_id}
            className={`p-5 rounded-2xl border bg-card shadow-card card-hover ${tenant.is_blocked ? 'opacity-70 border-red-300 dark:border-red-800' : ''}`}
            data-testid={`tenant-card-${tenant.tenant_id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {tenant.logo_url ? (
                  <img 
                    src={tenant.logo_url} 
                    alt={tenant.name} 
                    className="h-10 w-auto max-w-[80px] object-contain"
                  />
                ) : tenant.branding?.primary_color ? (
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: tenant.branding.primary_color }}
                  >
                    {tenant.name?.charAt(0) || "S"}
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center text-white font-bold">
                    {tenant.name?.charAt(0) || "S"}
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    {tenant.name}
                    {tenant.is_blocked && <Ban className="w-4 h-4 text-red-500" />}
                  </h3>
                  <p className="text-sm text-muted-foreground">/book/{tenant.slug}</p>
                  {/* NEW: vertical + plan badges */}
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid={`tenant-plan-row-${tenant.tenant_id}`}>
                    {tenant.vertical && (
                      <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 dark:from-slate-700 dark:to-slate-800 dark:text-slate-200">
                        {tenant.vertical}
                      </span>
                    )}
                    {tenant.selected_plan_id && (
                      <span className="inline-flex items-center text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" title={tenant.selected_plan_id}>
                        {tenant.selected_plan_id}
                      </span>
                    )}
                    {tenant.billing_cycle && (
                      <span className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                        {tenant.billing_cycle === "yearly" ? (language === "it" ? "Annuale" : "Yearly") : (language === "it" ? "Mensile" : "Monthly")}
                      </span>
                    )}
                    {tenant.expected_price != null && tenant.expected_price > 0 && (
                      <span className="inline-flex items-center text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        €{Number(tenant.expected_price).toLocaleString("it-IT", { maximumFractionDigits: 0 })}
                        {tenant.billing_cycle === "yearly" ? "/a" : "/m"}
                      </span>
                    )}
                    {tenant.landing_source && (
                      <span className="inline-flex items-center text-[10px] text-slate-500" title={language === "it" ? "Landing di provenienza" : "Landing source"}>
                        /{tenant.landing_source}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(tenant.subscription_status, tenant.is_blocked)}`}>
                {tenant.is_blocked ? (language === "it" ? "Bloccato" : "Blocked") : t(tenant.subscription_status)}
              </span>
            </div>

            <div className="space-y-2 text-sm">
              {tenant.phone && (
                <p className="text-muted-foreground">{tenant.phone}</p>
              )}
              {tenant.address && (
                <p className="text-muted-foreground">{tenant.address}</p>
              )}
              
              {/* Plan Type Badge */}
              <div className="flex items-center gap-2">
                {tenant.plan_type === "multi_sede" ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-violet-500/20 to-purple-500/20 text-violet-700 dark:text-violet-300 text-xs font-medium border border-violet-300 dark:border-violet-700">
                    <Building2 className="w-3 h-3" />
                    Multi-sede ({(tenant.locations || []).length}/{tenant.max_locations || 10})
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs font-medium">
                    <MapPin className="w-3 h-3" />
                    Mono-sede
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t">
                <div>
                  <p className="text-xs text-muted-foreground">
                    {language === "it" ? "Creato il" : "Created"}
                  </p>
                  <p className="font-medium text-sm">{formatDate(tenant.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {language === "it" ? "Scadenza" : "Expires"}
                  </p>
                  <p className="font-medium text-sm">{formatDate(tenant.subscription_end_date || tenant.subscription_expires_at)}</p>
                </div>
              </div>
              <p className="text-muted-foreground">
                {language === "it" ? "Fatturazione:" : "Billing:"} <span className="font-medium">{(tenant.billing_period || tenant.subscription_plan) === "annual" ? (language === "it" ? "Annuale" : "Annual") : (language === "it" ? "Mensile" : "Monthly")}</span>
              </p>
            </div>

            <div className="mt-4 pt-4 border-t space-y-3">
              {/* Subscription Controls */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline"
                  size="sm"
                  className={(tenant.free_forever || tenant.is_free) ? "border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20" : ""}
                  onClick={() => handleToggleFreeForever(tenant)}
                  data-testid={`free-toggle-${tenant.tenant_id}`}
                >
                  <Gift className="w-4 h-4 mr-1" />
                  {(tenant.free_forever || tenant.is_free)
                    ? (language === "it" ? "Rimetti a Pagamento" : "Set as Paid")
                    : (language === "it" ? "Rendi Gratis" : "Make Free")
                  }
                </Button>
                {(tenant.free_forever || tenant.is_free) && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 flex items-center">
                    <Gift className="w-3 h-3 mr-1" />
                    {language === "it" ? "Gratis" : "Free"}
                  </span>
                )}
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => handleExtendTrial(tenant)}
                  data-testid={`extend-trial-${tenant.tenant_id}`}
                >
                  <Clock className="w-4 h-4 mr-1" />
                  {language === "it" ? "+14 giorni" : "+14 days"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={tenant.custom_pricing?.enabled ? "border-violet-500 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20" : ""}
                  onClick={() => openBillingDialog(tenant)}
                  data-testid={`billing-tenant-${tenant.tenant_id}`}
                >
                  <Wallet className="w-4 h-4 mr-1" />
                  {language === "it" ? "Pricing" : "Pricing"}
                </Button>
                {tenant.custom_pricing?.enabled && (
                  <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-violet-100 to-fuchsia-100 text-violet-700 dark:from-violet-900/30 dark:to-fuchsia-900/30 dark:text-violet-300 inline-flex items-center gap-1" data-testid={`custom-badge-${tenant.tenant_id}`}>
                    <Tag className="w-3 h-3" />
                    {tenant.custom_pricing.plan_label || (language === "it" ? "Custom" : "Custom")}
                    <span className="font-mono text-[10px]">
                      · €{Number(tenant.custom_pricing.monthly_price || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })}/m
                      {tenant.custom_pricing.annual_price ? ` · €${Number(tenant.custom_pricing.annual_price).toLocaleString("it-IT", { maximumFractionDigits: 0 })}/a` : ""}
                    </span>
                  </span>
                )}
                <Button 
                  variant={tenant.is_blocked ? "outline" : "destructive"}
                  size="sm"
                  onClick={() => {
                    setSelectedTenant(tenant);
                    setBlockDialogOpen(true);
                  }}
                  data-testid={`block-tenant-${tenant.tenant_id}`}
                >
                  {tenant.is_blocked ? (
                    <>
                      <Power className="w-4 h-4 mr-1" />
                      {language === "it" ? "Attiva" : "Activate"}
                    </>
                  ) : (
                    <>
                      <Ban className="w-4 h-4 mr-1" />
                      {language === "it" ? "Disattiva" : "Deactivate"}
                    </>
                  )}
                </Button>
                <Button 
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  onClick={() => {
                    setSelectedTenant(tenant);
                    setDeleteDialogOpen(true);
                  }}
                  data-testid={`delete-tenant-${tenant.tenant_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              {/* View Actions */}
              <div className="flex gap-2">
                <Button 
                  variant="default" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => {
                    setManageTenant(tenant);
                    setManageDialogOpen(true);
                  }}
                  data-testid={`manage-tenant-${tenant.tenant_id}`}
                >
                  <Settings className="w-4 h-4 mr-1" />
                  {language === "it" ? "Gestisci" : "Manage"}
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a 
                    href={`/book/${tenant.slug}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    setSelectedTenant(tenant);
                    setAnalyticsDialogOpen(true);
                  }}
                  data-testid={`analytics-tenant-${tenant.tenant_id}`}
                >
                  <BarChart3 className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openPasswordResetDialog(tenant)}
                  title={language === "it" ? "Reset password admin tenant" : "Reset tenant admin password"}
                  data-testid={`reset-password-tenant-${tenant.tenant_id}`}
                >
                  <KeyRound className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openPlanRepairDialog(tenant)}
                  title={language === "it" ? "Ripara piano (riallinea features)" : "Repair plan (sync features)"}
                  data-testid={`repair-plan-tenant-${tenant.tenant_id}`}
                >
                  <Sparkles className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tenant Manage Dialog */}
      <TenantManageDialog 
        tenant={manageTenant}
        open={manageDialogOpen}
        onOpenChange={setManageDialogOpen}
        onTenantUpdate={(updated) => {
          setManageTenant(updated);
          setTenants(prev => prev.map(t => t.tenant_id === updated.tenant_id ? { ...t, ...updated } : t));
        }}
      />

      {/* Password Reset Dialog */}
      <Dialog open={passwordResetOpen} onOpenChange={setPasswordResetOpen}>
        <DialogContent className="max-w-md" data-testid="password-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-500" />
              {language === "it" ? "Reset Password" : "Reset Password"}
            </DialogTitle>
            <DialogDescription>
              {selectedTenant?.name}
              {selectedTenant?.slug && <span className="font-mono text-xs ml-2 opacity-60">/{selectedTenant.slug}</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                {language === "it" ? "Utente" : "User"}
              </label>
              {passwordResetUsers.length === 0 ? (
                <p className="text-sm text-slate-400">{language === "it" ? "Caricamento utenti…" : "Loading users…"}</p>
              ) : (
                <select
                  value={passwordResetTargetUserId}
                  onChange={(e) => setPasswordResetTargetUserId(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 px-3 py-2 text-sm"
                  data-testid="password-reset-user-select"
                >
                  {passwordResetUsers.map(u => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.email} · {u.role} {u.first_name ? `· ${u.first_name} ${u.last_name || ""}` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                {language === "it" ? "Nuova password (min 8 char)" : "New password (min 8 chars)"}
              </label>
              <Input
                type="text"
                value={passwordResetValue}
                onChange={(e) => setPasswordResetValue(e.target.value)}
                placeholder="••••••••"
                className="font-mono"
                data-testid="password-reset-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {language === "it"
                  ? "L'utente sarà disconnesso da tutte le sessioni attive e dovrà rifare login."
                  : "User will be logged out of all active sessions."}
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPasswordResetOpen(false)}
                    data-testid="password-reset-cancel">
              {language === "it" ? "Annulla" : "Cancel"}
            </Button>
            <Button
              onClick={submitPasswordReset}
              disabled={passwordResetSubmitting || passwordResetValue.length < 8 || !passwordResetTargetUserId}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white"
              data-testid="password-reset-submit"
            >
              <KeyRound className="w-4 h-4 mr-1.5" />
              {passwordResetSubmitting
                ? (language === "it" ? "Aggiornamento…" : "Updating…")
                : (language === "it" ? "Aggiorna password" : "Update password")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Repair Dialog */}
      <Dialog open={planRepairOpen} onOpenChange={setPlanRepairOpen}>
        <DialogContent className="max-w-lg" data-testid="plan-repair-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" />
              {language === "it" ? "Ripara piano tenant" : "Repair tenant plan"}
            </DialogTitle>
            <DialogDescription>
              {selectedTenant?.name}
              {selectedTenant?.slug && <span className="font-mono text-xs ml-2 opacity-60">/{selectedTenant.slug}</span>}
              <span className="block mt-1 text-[11px] text-slate-500">
                {language === "it"
                  ? "Riallinea vertical, tier, billing e features_enabled al piano scelto. Usalo quando un tenant ha feature lockate nonostante l'abbonamento attivo."
                  : "Re-sync vertical, tier, billing and features_enabled to the selected plan. Use when a tenant sees locked features despite an active subscription."}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                {language === "it" ? "Piano corrente" : "Current"}
              </label>
              <div className="text-xs font-mono bg-slate-50 dark:bg-white/5 rounded-lg p-2 border border-slate-200 dark:border-white/10">
                vertical: <b>{selectedTenant?.vertical || "—"}</b>
                {" · "}tier: <b>{selectedTenant?.subscription_tier || "—"}</b>
                {" · "}plan_id: <b>{selectedTenant?.selected_plan_id || "—"}</b>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                {language === "it" ? "Nuovo piano" : "New plan"}
              </label>
              {planRepairPlans.length === 0 ? (
                <p className="text-sm text-slate-400">{language === "it" ? "Caricamento…" : "Loading…"}</p>
              ) : (
                <select
                  value={planRepairTarget}
                  onChange={(e) => setPlanRepairTarget(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 px-3 py-2 text-sm"
                  data-testid="plan-repair-select"
                >
                  <option value="">— Seleziona piano —</option>
                  {planRepairPlans.map(p => (
                    <option key={p.plan_id} value={p.plan_id}>
                      {p.name} · €{p.price}/{p.billing === "monthly" ? "mese" : "anno"} · ({p.plan_id})
                    </option>
                  ))}
                </select>
              )}
            </div>
            <p className="text-[10px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-900/40 rounded-lg px-3 py-2">
              ⚠ {language === "it"
                ? "L'azione sovrascrive vertical/tier/features. Il cliente vedrà le nuove feature al prossimo refresh o login."
                : "Action overwrites vertical/tier/features. User will see new features on next refresh or login."}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPlanRepairOpen(false)} data-testid="plan-repair-cancel">
              {language === "it" ? "Annulla" : "Cancel"}
            </Button>
            <Button
              onClick={submitPlanRepair}
              disabled={planRepairSubmitting || !planRepairTarget}
              className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white"
              data-testid="plan-repair-submit"
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              {planRepairSubmitting
                ? (language === "it" ? "Riparando…" : "Repairing…")
                : (language === "it" ? "Ripara piano" : "Repair plan")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Billing Profile Dialog (Tier + Custom Pricing) */}
      <Dialog open={billingDialogOpen} onOpenChange={setBillingDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="billing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-violet-500" />
              {language === "it" ? "Pricing & Piano" : "Pricing & Plan"}
            </DialogTitle>
            <DialogDescription>
              {selectedTenant?.name}
              {selectedTenant?.slug && <span className="font-mono text-xs ml-2 opacity-60">/{selectedTenant.slug}</span>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Tier selector */}
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                {language === "it" ? "Tier abbonamento" : "Subscription tier"}
              </label>
              <Select value={billingForm.subscription_tier} onValueChange={handleBillingTierChange}>
                <SelectTrigger className="mt-1" data-testid="billing-tier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TIER_PRESETS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v.label} {v.monthly > 0 && <span className="text-xs text-slate-400 ml-2">€{v.monthly}/m · €{v.annual}/a</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom pricing toggle */}
            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-2xl border border-slate-200 dark:border-white/10 hover:border-violet-300 transition-colors" data-testid="billing-custom-toggle">
              <input
                type="checkbox"
                checked={billingForm.custom_enabled}
                onChange={(e) => setBillingForm(f => ({ ...f, custom_enabled: e.target.checked }))}
                className="mt-0.5 w-4 h-4 accent-violet-500"
              />
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                  {language === "it" ? "Prezzo personalizzato" : "Custom pricing"}
                </p>
                <p className="text-[11px] text-slate-500">
                  {language === "it"
                    ? "Sovrascrive il prezzo di listino del tier (MRR ricalcolato di conseguenza)."
                    : "Overrides tier list price (MRR is recalculated accordingly)."}
                </p>
              </div>
            </label>

            {billingForm.custom_enabled && (
              <div className="space-y-3 rounded-2xl border border-violet-200 dark:border-violet-900/30 bg-violet-50/40 dark:bg-violet-900/10 p-3" data-testid="billing-custom-fields">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    {language === "it" ? "Etichetta piano (visibile in dashboard)" : "Plan label"}
                  </label>
                  <Input
                    value={billingForm.plan_label}
                    onChange={(e) => setBillingForm(f => ({ ...f, plan_label: e.target.value }))}
                    placeholder="es. SmartBooker Dental Pro"
                    className="mt-1"
                    data-testid="billing-plan-label"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">€ / mese</label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={billingForm.monthly_price}
                      onChange={(e) => setBillingForm(f => ({ ...f, monthly_price: e.target.value }))}
                      placeholder="179.00"
                      className="mt-1 font-mono"
                      data-testid="billing-monthly-price"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">€ / anno</label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={billingForm.annual_price}
                      onChange={(e) => setBillingForm(f => ({ ...f, annual_price: e.target.value }))}
                      placeholder="1790.00"
                      className="mt-1 font-mono"
                      data-testid="billing-annual-price"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    {language === "it" ? "Ciclo di fatturazione contrattuale" : "Contracted billing cycle"}
                  </label>
                  <Select value={billingForm.billing_cycle} onValueChange={(v) => setBillingForm(f => ({ ...f, billing_cycle: v }))}>
                    <SelectTrigger className="mt-1" data-testid="billing-cycle-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">{language === "it" ? "Mensile" : "Monthly"}</SelectItem>
                      <SelectItem value="annual">{language === "it" ? "Annuale" : "Annual"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    {language === "it" ? "Note interne" : "Internal notes"}
                  </label>
                  <Input
                    value={billingForm.notes}
                    onChange={(e) => setBillingForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder={language === "it" ? "es. trattativa via sales, sconto early adopter" : "e.g. negotiated by sales"}
                    className="mt-1"
                    data-testid="billing-notes"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBillingDialogOpen(false)} disabled={billingSaving}>
              {language === "it" ? "Annulla" : "Cancel"}
            </Button>
            <Button onClick={handleSaveBilling} disabled={billingSaving} className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white" data-testid="billing-save">
              {billingSaving
                ? (language === "it" ? "Salvataggio…" : "Saving…")
                : (language === "it" ? "Salva" : "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedTenant?.is_blocked 
                ? (language === "it" ? "Sblocca Attività" : "Unblock Business")
                : (language === "it" ? "Blocca Attività" : "Block Business")
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-muted-foreground">
              {selectedTenant?.is_blocked 
                ? (language === "it" 
                    ? `Vuoi sbloccare "${selectedTenant?.name}"? L'attività potrà riprendere le operazioni.`
                    : `Do you want to unblock "${selectedTenant?.name}"? The business will be able to resume activities.`)
                : (language === "it" 
                    ? `Vuoi bloccare "${selectedTenant?.name}"? L'attività non potrà più accettare prenotazioni.`
                    : `Do you want to block "${selectedTenant?.name}"? The business will no longer accept bookings.`)
              }
            </p>
            {!selectedTenant?.is_blocked && (
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {language === "it" ? "Motivo del blocco (opzionale)" : "Block reason (optional)"}
                </label>
                <Input
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder={language === "it" ? "Es: Mancato pagamento" : "E.g.: Missed payment"}
                  data-testid="block-reason-input"
                />
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setBlockDialogOpen(false)}>
                {t("cancel")}
              </Button>
              <Button 
                variant={selectedTenant?.is_blocked ? "default" : "destructive"}
                onClick={handleBlockTenant}
                data-testid="confirm-block-btn"
              >
                {selectedTenant?.is_blocked 
                  ? (language === "it" ? "Sblocca" : "Unblock")
                  : (language === "it" ? "Blocca" : "Block")
                }
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {language === "it" ? "Elimina Attività" : "Delete Business"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-muted-foreground">
              {language === "it" 
                ? `Sei sicuro di voler eliminare "${selectedTenant?.name}"? Questa azione è irreversibile e cancellerà tutti i dati associati (appuntamenti, servizi, clienti, staff).`
                : `Are you sure you want to delete "${selectedTenant?.name}"? This action is irreversible and will delete all associated data (appointments, services, clients, staff).`
              }
            </p>
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                {language === "it" 
                  ? "⚠️ Tutti i dati verranno eliminati permanentemente!"
                  : "⚠️ All data will be permanently deleted!"
                }
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                {t("cancel")}
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteTenant}
                data-testid="confirm-delete-btn"
              >
                {language === "it" ? "Elimina Definitivamente" : "Delete Permanently"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tenant Analytics Dialog */}
      <Dialog open={analyticsDialogOpen} onOpenChange={setAnalyticsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-violet-500" />
              Analytics - {selectedTenant?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedTenantAnalytics ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                    <p className="text-sm opacity-80">{language === "it" ? "Incassi Totali" : "Total Revenue"}</p>
                    <p className="text-2xl font-bold">€{selectedTenantAnalytics.total_revenue?.toFixed(2) || "0.00"}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white">
                    <p className="text-sm opacity-80">{language === "it" ? "Appuntamenti" : "Appointments"}</p>
                    <p className="text-2xl font-bold">{selectedTenantAnalytics.total_appointments || 0}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                    <p className="text-sm opacity-80">{language === "it" ? "Clienti" : "Clients"}</p>
                    <p className="text-2xl font-bold">{selectedTenantAnalytics.total_clients || 0}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 text-white">
                    <p className="text-sm opacity-80">{language === "it" ? "Servizi" : "Services"}</p>
                    <p className="text-2xl font-bold">{selectedTenantAnalytics.total_services || 0}</p>
                  </div>
                </div>

                {/* Top Services */}
                {selectedTenantAnalytics.top_services?.length > 0 && (
                  <div className="p-4 rounded-xl border bg-card">
                    <h4 className="font-medium mb-3">{language === "it" ? "Servizi Più Richiesti" : "Top Services"}</h4>
                    <div className="space-y-2">
                      {selectedTenantAnalytics.top_services.map((service, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span>{service.name}</span>
                          <span className="text-muted-foreground">{service.count} {language === "it" ? "prenotazioni" : "bookings"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-muted-foreground mt-2">{language === "it" ? "Caricamento..." : "Loading..."}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {tenants.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground">
            {language === "it" ? "Nessuna attività. Crea la prima!" : "No businesses. Create the first one!"}
          </p>
        </div>
      )}
    </div>
  );
};

const SubscriptionsManagement = () => {
  const { language } = useLanguage();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/tenants`, { withCredentials: true });
      setTenants(response.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Single subscription plan
  const plan = {
    name: "SmartBooker",
    monthly: 34.95,
    yearly: 349
  };

  const handleUpgrade = async (tenantId, billing) => {
    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        tenant_id: tenantId,
        billing,
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      window.open(response.data.checkout_url, "_blank");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscriptions-management">
      <PlanStatsGrid />

      {/* Tenants Subscriptions */}
      <div className="bg-card rounded-2xl border overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>{language === "it" ? "Attività" : "Business"}</th>
              <th>Status</th>
              <th>{language === "it" ? "Azioni" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr key={tenant.tenant_id}>
                <td>
                  <div>
                    <p className="font-medium">{tenant.name}</p>
                    <p className="text-sm text-muted-foreground">{tenant.slug}</p>
                  </div>
                </td>
                <td>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    tenant.subscription_status === "active" ? "badge-success" :
                    tenant.subscription_status === "trial" ? "badge-warning" : "badge-error"
                  }`}>
                    {tenant.subscription_status}
                  </span>
                </td>
                <td>
                  {tenant.subscription_status !== "active" && (
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleUpgrade(tenant.tenant_id, "monthly")}
                      >
                        €34,95/{language === "it" ? "mese" : "mo"}
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => handleUpgrade(tenant.tenant_id, "yearly")}
                      >
                        €349/{language === "it" ? "anno" : "yr"}
                      </Button>
                    </div>
                  )}
                  {tenant.subscription_status === "active" && (
                    <span className="text-sm text-emerald-600 font-medium">
                      {language === "it" ? "Attivo" : "Active"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================
// Plan Stats Grid — Premium SaaS card per piano
// ============================================================
const PLAN_GRADIENTS = {
  essential: "from-slate-700 via-slate-800 to-slate-900",
  growth: "from-blue-600 via-indigo-600 to-violet-700",
  pro: "from-violet-600 via-fuchsia-600 to-pink-600",
  dental_pro: "from-cyan-600 via-teal-600 to-emerald-600",
  custom: "from-amber-600 via-orange-600 to-red-600",
};
const PLAN_ACCENTS = {
  essential: "text-slate-400",
  growth: "text-violet-400",
  pro: "text-pink-400",
  dental_pro: "text-emerald-400",
  custom: "text-orange-400",
};

const PlanStatsGrid = () => {
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/subscription/admin/plan-stats?period=${period}`, { withCredentials: true })
      .then(r => { if (mounted) setStats(r.data); })
      .catch(() => { if (mounted) toast.error(language === "it" ? "Errore caricamento piani" : "Plan stats error"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [period, language]);

  if (loading) return <div className="h-32 flex items-center justify-center"><div className="animate-spin w-6 h-6 border-2 border-accent border-t-transparent rounded-full" /></div>;
  if (!stats) return null;

  const totals = stats.totals;

  return (
    <div className="space-y-4" data-testid="plan-stats-grid">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">{language === "it" ? "Piani SmartBooker" : "Plans"}</h2>
          <p className="text-xs text-muted-foreground">{language === "it" ? "Performance per piano" : "Performance per plan"}</p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-xs rounded-full font-medium transition-all ${period === p ? "bg-accent text-accent-foreground shadow" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
              data-testid={`plan-stats-period-${p}`}
            >
              {p}gg
            </button>
          ))}
        </div>
      </div>

      {/* Totals Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 p-4 rounded-2xl bg-gradient-to-br from-zinc-900 via-slate-900 to-zinc-900 text-white" data-testid="plan-stats-totals">
        <TotalsItem label={language === "it" ? "Clienti attivi" : "Active"} value={totals.active_clients} />
        <TotalsItem label="MRR" value={`€${(totals.mrr || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`} accent="text-emerald-400" />
        <TotalsItem label="ARR" value={`€${(totals.arr || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`} accent="text-violet-400" />
        <TotalsItem label={language === "it" ? "Conversioni" : "Conversions"} value={totals.conversions} accent="text-amber-400" />
        <TotalsItem label={`Revenue ${period}gg`} value={`€${(totals.revenue_period || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`} accent="text-pink-400" />
        <TotalsItem label="Churn" value={totals.churn_count} accent="text-rose-400" />
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {stats.plans.map(p => {
          const grad = PLAN_GRADIENTS[p.key] || PLAN_GRADIENTS.custom;
          const accent = PLAN_ACCENTS[p.key] || PLAN_ACCENTS.custom;
          return (
            <div
              key={p.key}
              className={`rounded-2xl p-4 bg-gradient-to-br ${grad} text-white relative overflow-hidden hover:shadow-xl hover:scale-[1.02] transition-all`}
              data-testid={`plan-card-${p.key}`}
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/5 blur-2xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-baseline justify-between mb-1">
                  <h3 className="font-bold text-lg" data-testid={`plan-label-${p.key}`}>{p.label}</h3>
                  <span className="text-[10px] font-bold uppercase tracking-widest opacity-70">{p.key}</span>
                </div>
                <div className="flex items-baseline gap-1 mb-3">
                  {p.monthly_price > 0 ? (
                    <>
                      <span className="text-2xl font-bold">€{p.monthly_price}</span>
                      <span className="text-xs opacity-70">/{language === "it" ? "mese" : "mo"}</span>
                    </>
                  ) : (
                    <span className="text-base font-medium opacity-90">{language === "it" ? "Su misura" : "Custom"}</span>
                  )}
                </div>
                {p.annual_price > 0 && (
                  <p className="text-[10px] opacity-70 mb-3">€{p.annual_price}/{language === "it" ? "anno" : "yr"}</p>
                )}

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Stat label={language === "it" ? "Clienti" : "Clients"} value={p.active_clients} accent={accent} />
                  <Stat label="MRR" value={`€${Math.round(p.mrr).toLocaleString("it-IT")}`} accent={accent} />
                  <Stat label="ARR" value={`€${Math.round(p.arr).toLocaleString("it-IT")}`} accent={accent} />
                  <Stat label={language === "it" ? "Conversioni" : "Conv."} value={p.conversions} accent={accent} />
                </div>

                <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                  <span className="text-[10px] opacity-70">Churn</span>
                  <span className={`text-xs font-bold ${p.churn_rate > 5 ? "text-rose-300" : "text-emerald-300"}`}>
                    {p.churn_count} ({p.churn_rate}%)
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TotalsItem = ({ label, value, accent = "text-white" }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider opacity-70">{label}</p>
    <p className={`text-lg sm:text-xl font-bold ${accent}`}>{value}</p>
  </div>
);

const Stat = ({ label, value, accent }) => (
  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2">
    <p className="text-[9px] opacity-80">{label}</p>
    <p className={`text-sm font-bold ${accent}`}>{value}</p>
  </div>
);


// Super Admin Analytics Component
const SuperAdminAnalytics = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [tenantAnalytics, setTenantAnalytics] = useState(null);

  useEffect(() => {
    fetchGlobalAnalytics();
    fetchTenants();
  }, []);

  const fetchGlobalAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/global`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/tenants`, { withCredentials: true });
      setTenants(response.data);
    } catch (error) {
      console.error("Error fetching tenants");
    }
  };

  const fetchTenantAnalytics = async (tenantId) => {
    try {
      const response = await axios.get(`${API}/analytics/tenant/${tenantId}`, { withCredentials: true });
      setTenantAnalytics(response.data);
    } catch (error) {
      toast.error(language === "it" ? "Errore" : "Error");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Global Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Building2 className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-violet-100 text-sm">{language === "it" ? "Clienti Registrati" : "Registered Clients"}</p>
          <p className="text-3xl font-bold mt-1">{data?.total_tenants || 0}</p>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Euro className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-emerald-100 text-sm">{language === "it" ? "Incassi Totali" : "Total Revenue"}</p>
          <p className="text-3xl font-bold mt-1">{formatCurrency(data?.total_revenue)}</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Calendar className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-blue-100 text-sm">{language === "it" ? "Appuntamenti Totali" : "Total Appointments"}</p>
          <p className="text-3xl font-bold mt-1">{data?.total_appointments || 0}</p>
        </div>

        <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Users className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-pink-100 text-sm">{language === "it" ? "Clienti Totali" : "Total Clients"}</p>
          <p className="text-3xl font-bold mt-1">{data?.total_clients || 0}</p>
        </div>
      </div>

      {/* Top Clients */}
      {data?.top_salons?.length > 0 && (
        <div className="bg-card rounded-2xl border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            {language === "it" ? "Top Clienti per Incassi" : "Top Clients by Revenue"}
          </h2>
          <div className="space-y-3">
            {data.top_salons.map((salon, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                    idx === 0 ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                    idx === 1 ? 'bg-gradient-to-r from-slate-400 to-slate-500' :
                    idx === 2 ? 'bg-gradient-to-r from-amber-700 to-amber-800' :
                    'bg-slate-400'
                  }`}>
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-medium">{salon.name}</p>
                    <p className="text-xs text-muted-foreground">{salon.appointments} {language === "it" ? "appuntamenti" : "appointments"}</p>
                  </div>
                </div>
                <p className="font-semibold text-emerald-600">{formatCurrency(salon.revenue)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client Selector */}
      <div className="bg-card rounded-2xl border p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5 text-blue-500" />
          {language === "it" ? "Analytics per Cliente" : "Analytics by Client"}
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          {/* Client List */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {tenants.map((tenant) => (
              <button
                key={tenant.tenant_id}
                onClick={() => {
                  setSelectedTenantId(tenant.tenant_id);
                  fetchTenantAnalytics(tenant.tenant_id);
                }}
                className={`w-full p-3 rounded-xl text-left transition-all ${
                  selectedTenantId === tenant.tenant_id 
                    ? 'bg-violet-500 text-white' 
                    : 'bg-muted/30 hover:bg-muted/50'
                }`}
              >
                <p className="font-medium">{tenant.name}</p>
                <p className={`text-xs ${selectedTenantId === tenant.tenant_id ? 'text-violet-100' : 'text-muted-foreground'}`}>
                  /book/{tenant.slug}
                </p>
              </button>
            ))}
          </div>

          {/* Selected Client Analytics */}
          <div className="p-4 rounded-xl border bg-muted/20">
            {selectedTenantId && tenantAnalytics ? (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">{tenantAnalytics.salon_name}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                    <p className="text-xs text-muted-foreground">{language === "it" ? "Incassi" : "Revenue"}</p>
                    <p className="text-lg font-bold text-emerald-600">{formatCurrency(tenantAnalytics.total_revenue)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20">
                    <p className="text-xs text-muted-foreground">{language === "it" ? "Appuntamenti" : "Appointments"}</p>
                    <p className="text-lg font-bold text-violet-600">{tenantAnalytics.total_appointments}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                    <p className="text-xs text-muted-foreground">{language === "it" ? "Clienti" : "Clients"}</p>
                    <p className="text-lg font-bold text-blue-600">{tenantAnalytics.total_clients}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-pink-50 dark:bg-pink-900/20">
                    <p className="text-xs text-muted-foreground">{language === "it" ? "Servizi" : "Services"}</p>
                    <p className="text-lg font-bold text-pink-600">{tenantAnalytics.total_services}</p>
                  </div>
                </div>
                {tenantAnalytics.top_services?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">{language === "it" ? "Top Servizi" : "Top Services"}</p>
                    {tenantAnalytics.top_services.slice(0, 3).map((s, i) => (
                      <div key={i} className="flex justify-between text-sm py-1">
                        <span>{s.name}</span>
                        <span className="text-muted-foreground">{s.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {language === "it" 
                  ? "Seleziona un cliente per vedere le analytics" 
                  : "Select a client to view analytics"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SALES_LEAD_STATUSES = [
  { value: "new",            label_it: "Nuovi Lead",                 label_en: "New",              color: "bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300" },
  { value: "contacted",      label_it: "Contattati",                 label_en: "Contacted",        color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  { value: "interested",     label_it: "Interessati",                label_en: "Interested",       color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
  { value: "demo_booked",    label_it: "Demo Fissata",               label_en: "Demo Booked",      color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
  { value: "trial",          label_it: "Trial / Chiusura",           label_en: "Trial / Closing",  color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
  { value: "closed_won",     label_it: "Chiuso ✓",                   label_en: "Closed Won",       color: "bg-emerald-200 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
  { value: "recall_later",   label_it: "Da risentire più avanti",    label_en: "Recall Later",     color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" },
  { value: "no_reply",       label_it: "Mai Risposto",               label_en: "No Reply",         color: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300" },
  { value: "not_interested", label_it: "Non Interessato",            label_en: "Not Interested",   color: "bg-zinc-100 text-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-300" },
];

const SalesManagement = () => {
  const { language } = useLanguage();
  const [overview, setOverview] = useState(null);
  const [salesUsers, setSalesUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "", commission_rate: 10, commission_type: "percentage" });
  const [creating, setCreating] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null);
  const [unassignedLeads, setUnassignedLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [overviewRes, usersRes] = await Promise.all([
        axios.get(`${API}/sales/overview`, { withCredentials: true }),
        axios.get(`${API}/sales/users`, { withCredentials: true }),
      ]);
      setOverview(overviewRes.data);
      setSalesUsers(usersRes.data.sales_users);
    } catch {
      toast.error(language === "it" ? "Errore" : "Error");
    } finally {
      setLoading(false);
    }
  };

  const createSalesUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error(language === "it" ? "Compila tutti i campi" : "Fill all fields");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/sales/users`, createForm, { withCredentials: true });
      toast.success(language === "it" ? "Sales creato!" : "Sales created!");
      setShowCreate(false);
      setCreateForm({ name: "", email: "", password: "", commission_rate: 10, commission_type: "percentage" });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (userId, current) => {
    try {
      await axios.patch(`${API}/sales/users/${userId}`, { is_active: !current }, { withCredentials: true });
      fetchAll();
    } catch {
      toast.error("Error");
    }
  };

  const openAssign = async (salesUser) => {
    setAssignDialog(salesUser);
    setSelectedLeads([]);
    try {
      const res = await axios.get(`${API}/leads/list`, { withCredentials: true });
      setUnassignedLeads(res.data.leads.filter(l => !l.assigned_to));
    } catch {
      toast.error("Error");
    }
  };

  const assignBulk = async () => {
    if (!assignDialog || selectedLeads.length === 0) return;
    setAssigning(true);
    try {
      await axios.post(`${API}/sales/assign-bulk`, {
        lead_ids: selectedLeads,
        sales_user_id: assignDialog.user_id,
      }, { withCredentials: true });
      toast.success(language === "it" ? `${selectedLeads.length} lead assegnati` : `${selectedLeads.length} leads assigned`);
      setAssignDialog(null);
      fetchAll();
    } catch {
      toast.error("Error");
    } finally {
      setAssigning(false);
    }
  };

  const fmt = (v) => `€${(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 0 })}`;

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="sales-management">
      {/* Overview */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[
            { label: language === "it" ? "Totale Lead" : "Total Leads", value: overview.total_leads, color: "text-slate-700 dark:text-slate-300" },
            { label: language === "it" ? "Assegnati" : "Assigned", value: overview.assigned, color: "text-blue-700 dark:text-blue-300" },
            { label: language === "it" ? "Non assegnati" : "Unassigned", value: overview.unassigned, color: "text-amber-700 dark:text-amber-300" },
            { label: language === "it" ? "Chiusi" : "Won", value: overview.closed_won, color: "text-emerald-700 dark:text-emerald-300" },
            { label: language === "it" ? "Valore Totale" : "Total Value", value: fmt(overview.total_value), color: "text-emerald-700 dark:text-emerald-300" },
            { label: language === "it" ? "Commissioni" : "Commissions", value: fmt(overview.total_commissions), color: "text-violet-700 dark:text-violet-300" },
          ].map((s, i) => (
            <div key={i} className="p-3 rounded-xl bg-muted/50 text-center">
              <p className={`text-xl font-bold ${s.color}`} data-testid={`sales-overview-${i}`}>{s.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Create Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">{language === "it" ? "Team Sales" : "Sales Team"}</h3>
        <Button onClick={() => setShowCreate(!showCreate)} className="rounded-full" size="sm" data-testid="create-sales-btn">
          <Plus className="w-4 h-4 mr-2" />{language === "it" ? "Nuovo Sales" : "New Sales"}
        </Button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="p-6 rounded-xl border bg-card space-y-4" data-testid="create-sales-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">{language === "it" ? "Nome" : "Name"}</label>
              <Input value={createForm.name} onChange={(e) => setCreateForm(p => ({ ...p, name: e.target.value }))} placeholder="Marco Rossi" className="rounded-lg" data-testid="create-sales-name" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Email</label>
              <Input type="email" value={createForm.email} onChange={(e) => setCreateForm(p => ({ ...p, email: e.target.value }))} placeholder="marco@smartbooker.it" className="rounded-lg" data-testid="create-sales-email" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Password</label>
              <Input type="text" value={createForm.password} onChange={(e) => setCreateForm(p => ({ ...p, password: e.target.value }))} placeholder="Password" className="rounded-lg" data-testid="create-sales-password" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">{language === "it" ? "Commissione (%)" : "Commission (%)"}</label>
              <Input type="number" value={createForm.commission_rate} onChange={(e) => setCreateForm(p => ({ ...p, commission_rate: parseFloat(e.target.value) || 0 }))} className="rounded-lg" data-testid="create-sales-rate" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{language === "it" ? "Tipo" : "Type"}</label>
              <Select value={createForm.commission_type} onValueChange={(v) => setCreateForm(p => ({ ...p, commission_type: v }))}>
                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">{language === "it" ? "Percentuale" : "Percentage"}</SelectItem>
                  <SelectItem value="fixed">{language === "it" ? "Fisso (€)" : "Fixed (€)"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="rounded-full">{language === "it" ? "Annulla" : "Cancel"}</Button>
            <Button onClick={createSalesUser} disabled={creating} className="rounded-full" data-testid="create-sales-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {language === "it" ? "Crea" : "Create"}
            </Button>
          </div>
        </div>
      )}

      {/* Sales Users Table */}
      <div className="rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="sales-users-table">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left py-3 px-4 text-sm font-medium">{language === "it" ? "Nome" : "Name"}</th>
                <th className="text-left py-3 px-4 text-sm font-medium hide-mobile">Email</th>
                <th className="text-center py-3 px-4 text-sm font-medium">{language === "it" ? "Lead" : "Leads"}</th>
                <th className="text-center py-3 px-4 text-sm font-medium hide-mobile">{language === "it" ? "Chiusi" : "Won"}</th>
                <th className="text-center py-3 px-4 text-sm font-medium hide-mobile">{language === "it" ? "Conv." : "Conv."}</th>
                <th className="text-center py-3 px-4 text-sm font-medium">{language === "it" ? "Commissioni" : "Commissions"}</th>
                <th className="text-left py-3 px-4 text-sm font-medium">{language === "it" ? "Azioni" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {salesUsers.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">{language === "it" ? "Nessun sales" : "No sales"}</td></tr>
              ) : salesUsers.map(su => (
                <tr key={su.user_id} className={`border-t transition-colors ${su.is_active ? "hover:bg-muted/30" : "opacity-50"}`} data-testid={`sales-user-row-${su.user_id}`}>
                  <td className="py-3 px-4">
                    <p className="font-medium text-sm">{su.name}</p>
                    <p className="text-xs text-muted-foreground">{su.commission_rate}% {language === "it" ? "commissione" : "commission"}</p>
                  </td>
                  <td className="py-3 px-4 hide-mobile text-sm text-muted-foreground">{su.email}</td>
                  <td className="py-3 px-4 text-center font-medium">{su.lead_stats?.total_assigned || 0}</td>
                  <td className="py-3 px-4 text-center hide-mobile font-medium text-emerald-600">{su.lead_stats?.closed_won || 0}</td>
                  <td className="py-3 px-4 text-center hide-mobile text-sm">{su.lead_stats?.conversion_rate || 0}%</td>
                  <td className="py-3 px-4 text-center font-bold text-emerald-600">{fmt(su.total_commission_earned)}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-8 rounded-full text-xs" onClick={() => openAssign(su)} data-testid={`assign-btn-${su.user_id}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" />{language === "it" ? "Assegna" : "Assign"}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-full" onClick={() => toggleActive(su.user_id, su.is_active)}>
                        <Power className={`w-4 h-4 ${su.is_active ? "text-emerald-500" : "text-red-500"}`} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={(open) => !open && setAssignDialog(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {language === "it" ? "Assegna lead a" : "Assign leads to"} {assignDialog?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {unassignedLeads.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">{language === "it" ? "Nessun lead non assegnato" : "No unassigned leads"}</p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{unassignedLeads.length} {language === "it" ? "lead disponibili" : "available leads"}</p>
                  <Button variant="ghost" size="sm" className="rounded-full text-xs" onClick={() => setSelectedLeads(selectedLeads.length === unassignedLeads.length ? [] : unassignedLeads.map(l => l.lead_id))}>
                    {selectedLeads.length === unassignedLeads.length ? (language === "it" ? "Deseleziona" : "Deselect") : (language === "it" ? "Seleziona tutti" : "Select all")}
                  </Button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {unassignedLeads.map(l => (
                    <label key={l.lead_id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedLeads.includes(l.lead_id) ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20" : "hover:bg-muted/30"}`}>
                      <input type="checkbox" checked={selectedLeads.includes(l.lead_id)} onChange={(e) => setSelectedLeads(p => e.target.checked ? [...p, l.lead_id] : p.filter(id => id !== l.lead_id))} className="rounded" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{l.name}</p>
                        <p className="text-xs text-muted-foreground">{l.phone} · {(SECTOR_LABELS[l.sector] || {})[language] || l.sector}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAssignDialog(null)} className="rounded-full">{language === "it" ? "Annulla" : "Cancel"}</Button>
              <Button onClick={assignBulk} disabled={assigning || selectedLeads.length === 0} className="rounded-full" data-testid="assign-confirm-btn">
                {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                {language === "it" ? `Assegna ${selectedLeads.length}` : `Assign ${selectedLeads.length}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Pipeline stages — SHARED with sales backend (`sales.py:STAGES`) and
// SalesDashboardV2 kanban. Any change here must mirror the other two.
const LEAD_STATUSES = [
  { value: "new",            label_it: "Nuovi Lead",                 label_en: "New",              color: "bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300" },
  { value: "contacted",      label_it: "Contattati",                 label_en: "Contacted",        color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  { value: "interested",     label_it: "Interessati",                label_en: "Interested",       color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
  { value: "demo_booked",    label_it: "Demo Fissata",               label_en: "Demo Booked",      color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
  { value: "trial",          label_it: "Trial / Chiusura",           label_en: "Trial / Closing",  color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
  { value: "closed_won",     label_it: "Chiuso ✓",                   label_en: "Closed Won",       color: "bg-emerald-200 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
  { value: "recall_later",   label_it: "Da risentire più avanti",    label_en: "Recall Later",     color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" },
  { value: "no_reply",       label_it: "Mai Risposto",               label_en: "No Reply",         color: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300" },
  { value: "not_interested", label_it: "Non Interessato",            label_en: "Not Interested",   color: "bg-zinc-100 text-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-300" },
  { value: "delete",         label_it: "Elimina",                    label_en: "Delete",           color: "bg-red-200 text-red-700 dark:bg-red-900/40 dark:text-red-400" },
];

const SECTOR_LABELS = {
  dentisti: { it: "Dentisti", en: "Dentists" },
  estetica: { it: "Estetica", en: "Beauty" },
  fisioterapia: { it: "Fisioterapia", en: "Physio" },
  medico: { it: "Medico", en: "Medical" },
  consulente: { it: "Consulente", en: "Consultant" },
  fitness: { it: "Fitness", en: "Fitness" },
  barber: { it: "Barbiere", en: "Barber" },
  dentist: { it: "Dentista", en: "Dentist" },
  beauty: { it: "Estetica", en: "Beauty" },
  physio: { it: "Fisioterapia", en: "Physio" },
  altro: { it: "Altro", en: "Other" },
};

// ============================================================
// Lead Detail Drawer (Super Admin read-only timeline)
// ============================================================
const ENTRY_TYPE_META = {
  call:     { label: "Call", labelEn: "Call", icon: PhoneCall, color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800/60" },
  message:  { label: "Messaggio", labelEn: "Message", icon: MessageSquare, color: "text-sky-600 bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-800/60" },
  followup: { label: "Follow-up", labelEn: "Follow-up", icon: Clock, color: "text-amber-600 bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800/60" },
  note:     { label: "Nota", labelEn: "Note", icon: StickyNote, color: "text-violet-600 bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-800/60" },
};

const SUPER_ADMIN_SOURCE_LABELS = {
  instagram: "Instagram", whatsapp: "WhatsApp", facebook: "Facebook", tiktok: "TikTok",
  email: "Mail", cold_call: "Call a freddo", outbound_manual: "Altro / Manuale",
  super_admin_manual: "Super Admin",
};

const LeadDetailDrawer = ({ lead, entries, loading, sectors, language, onClose, onEntriesRefresh }) => {
  const [adminNote, setAdminNote] = useState("");
  const [sendingNote, setSendingNote] = useState(false);

  const submitAdminNote = async () => {
    const text = adminNote.trim();
    if (!text || !lead?.lead_id) return;
    setSendingNote(true);
    try {
      const res = await axios.post(`${API}/leads/${lead.lead_id}/admin-note`, { content: text }, { withCredentials: true });
      if (res.data?.success) {
        toast.success(res.data.notified_sales
          ? (language === "it" ? "Nota inviata e sales notificato" : "Note sent and sales notified")
          : (language === "it" ? "Nota salvata (lead non assegnato)" : "Note saved (unassigned lead)")
        );
        setAdminNote("");
        if (onEntriesRefresh) await onEntriesRefresh();
      }
    } catch {
      toast.error(language === "it" ? "Errore invio nota" : "Error sending note");
    } finally {
      setSendingNote(false);
    }
  };

  if (!lead) return null;
  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(language === "it" ? "it-IT" : "en-US", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };
  const sectorLabel = (sectors[lead.sector] || {})[language] || lead.sector;
  const sourceLabel = SUPER_ADMIN_SOURCE_LABELS[lead.source_page] || lead.source_page || "—";

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="admin-lead-detail">
        <DialogHeader className="pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 pr-10 text-xl">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-md flex-shrink-0">
              <UserIcon className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate">{lead.name}</p>
              {lead.referent_name && (
                <p className="text-xs text-muted-foreground font-normal mt-0.5">
                  {language === "it" ? "Referente" : "Contact"}: <span className="font-medium text-foreground">{lead.referent_name}</span>
                </p>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-1 py-4 space-y-5" data-testid="admin-lead-detail-body">
          {/* Info chips */}
          <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="admin-lead-info-chips">
            {sectorLabel && <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-foreground font-medium capitalize">{sectorLabel}</span>}
            {lead.city && <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-foreground">{lead.city}</span>}
            {lead.interest_level && <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-foreground">Interesse {lead.interest_level}</span>}
            <span className="px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 font-medium">{sourceLabel}</span>
            {lead.assigned_sales_name && (
              <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 font-medium">
                Owner: {lead.assigned_sales_name}
              </span>
            )}
            {lead.status && (
              <span className="px-2 py-0.5 rounded-full bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-200 font-semibold">
                {lead.status}
              </span>
            )}
          </div>

          {/* Contacts */}
          <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 space-y-2" data-testid="admin-lead-contacts">
            {lead.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <a href={`tel:${lead.phone}`} className="font-medium hover:underline">{lead.phone}</a>
                <a href={`https://wa.me/${(lead.phone || "").replace(/[^0-9+]/g, "")}`} target="_blank" rel="noopener noreferrer"
                   className="ml-auto text-xs px-2 py-0.5 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 inline-flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> WhatsApp
                </a>
              </div>
            )}
            {lead.email && (
              <div className="flex items-center gap-2 text-sm" data-testid="admin-lead-email">
                <Mail className="w-4 h-4 text-sky-600 flex-shrink-0" />
                <a href={`mailto:${lead.email}`} className="font-medium hover:underline break-all">{lead.email}</a>
              </div>
            )}
            {lead.instagram_username && (
              <div className="flex items-center gap-2 text-sm">
                <Instagram className="w-4 h-4 text-pink-600 flex-shrink-0" />
                <a href={`https://instagram.com/${lead.instagram_username}`} target="_blank" rel="noopener noreferrer" className="font-medium hover:underline">@{lead.instagram_username}</a>
              </div>
            )}
          </div>

          {/* Form message + initial notes */}
          {(lead.message || lead.notes) && (
            <div className="grid md:grid-cols-2 gap-3">
              {lead.message && (
                <div className="p-3 rounded-2xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800/60" data-testid="admin-lead-message">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-sky-700 dark:text-sky-300 mb-1.5">
                    {language === "it" ? "Messaggio dal form" : "Form message"}
                  </p>
                  <p className="text-xs whitespace-pre-wrap leading-relaxed">{lead.message}</p>
                </div>
              )}
              {lead.notes && (
                <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60" data-testid="admin-lead-initial-notes">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300 mb-1.5 flex items-center gap-1">
                    <StickyNote className="w-3 h-3" /> {language === "it" ? "Nota iniziale" : "Initial note"}
                  </p>
                  <p className="text-xs whitespace-pre-wrap leading-relaxed">{lead.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Annotations timeline */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-violet-500" />
              <h3 className="font-bold text-sm">
                {language === "it" ? "Cronologia annotazioni" : "Annotations timeline"}
              </h3>
              <span className="text-xs text-muted-foreground">({entries.length})</span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {language === "it" ? "Caricamento..." : "Loading..."}
              </div>
            ) : entries.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground border border-dashed rounded-2xl" data-testid="admin-lead-no-entries">
                {language === "it"
                  ? "Nessuna annotazione ancora inserita dai sales."
                  : "No annotations yet from sales."}
              </div>
            ) : (
              <div className="space-y-2" data-testid="admin-lead-entries">
                {entries.map((e) => {
                  const isAdmin = e.is_admin_note || e.type === "admin_note";
                  const meta = isAdmin
                    ? { label: "Admin", labelEn: "Admin", icon: Shield, color: "text-rose-600 bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800/60" }
                    : (ENTRY_TYPE_META[e.type] || ENTRY_TYPE_META.note);
                  const Icon = meta.icon;
                  const authorName = isAdmin ? (e.admin_name || "Admin") : (e.sales_name || "Sales");
                  return (
                    <div key={e.entry_id} className={`p-3 rounded-xl border ${meta.color}`} data-testid={`admin-lead-entry-${e.entry_id}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-white dark:bg-slate-900 border flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap text-xs mb-1">
                            <span className="font-bold uppercase tracking-wider">{language === "it" ? meta.label : meta.labelEn}</span>
                            <span className="text-muted-foreground">·</span>
                            <span className="text-muted-foreground">{fmt(e.created_at)}</span>
                            <span className="text-muted-foreground">·</span>
                            <span className="font-medium text-foreground">{authorName}</span>
                          </div>
                          {e.content && <p className="text-sm whitespace-pre-wrap leading-relaxed">{e.content}</p>}
                          {e.followup_at && (
                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1.5 inline-flex items-center gap-1 font-medium">
                              <Clock className="w-3 h-3" /> Follow-up: {fmt(e.followup_at)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Admin-Note composer */}
            <div className="mt-4 p-3 rounded-2xl bg-rose-50/60 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-800/60" data-testid="admin-note-composer">
              <p className="text-[10px] uppercase tracking-wider font-bold text-rose-700 dark:text-rose-300 mb-2 flex items-center gap-1">
                <Shield className="w-3 h-3" />
                {language === "it" ? "Scrivi una nota al sales assegnato" : "Write a note to the assigned sales"}
              </p>
              <textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                rows={3}
                placeholder={language === "it"
                  ? "Es: Chiama entro oggi — ha espresso urgenza via DM Instagram..."
                  : "E.g.: Call today — urgent via Instagram DM..."}
                className="w-full rounded-lg border border-rose-200 dark:border-rose-800/60 bg-white dark:bg-slate-900 p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                data-testid="admin-note-textarea"
              />
              <div className="flex items-center justify-between gap-2 mt-2">
                <span className="text-[11px] text-muted-foreground">
                  {lead.assigned_sales_name
                    ? (language === "it" ? `Sarà notificato: ${lead.assigned_sales_name}` : `Will notify: ${lead.assigned_sales_name}`)
                    : (language === "it" ? "⚠️ Lead non assegnato — nessuno verrà notificato" : "⚠️ Unassigned lead — no one will be notified")}
                </span>
                <Button
                  size="sm"
                  onClick={submitAdminNote}
                  disabled={!adminNote.trim() || sendingNote}
                  className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="admin-note-submit"
                >
                  {sendingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Bell className="w-3.5 h-3.5 mr-1.5" />}
                  {language === "it" ? "Invia e notifica" : "Send & notify"}
                </Button>
              </div>
            </div>
          </div>

          {/* Status history */}
          {Array.isArray(lead.status_history) && lead.status_history.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-slate-500" />
                <h3 className="font-bold text-sm">
                  {language === "it" ? "Storico status" : "Status history"}
                </h3>
                <span className="text-xs text-muted-foreground">({lead.status_history.length})</span>
              </div>
              <div className="space-y-1.5" data-testid="admin-lead-history">
                {lead.status_history.slice().reverse().map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-800/40">
                    <span className="px-2 py-0.5 rounded-full bg-white dark:bg-slate-800 border font-medium">{h.from || "—"}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300 font-semibold">{h.to}</span>
                    <span className="text-muted-foreground ml-auto">{fmt(h.at)}</span>
                    {h.by_name && <span className="text-muted-foreground">· {h.by_name}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Created / updated footer */}
          <div className="pt-2 border-t text-xs text-muted-foreground flex items-center justify-between flex-wrap gap-2">
            <span>{language === "it" ? "Creato" : "Created"}: {fmt(lead.created_at)}</span>
            {lead.updated_at && <span>{language === "it" ? "Aggiornato" : "Updated"}: {fmt(lead.updated_at)}</span>}
            {lead.created_by_name && <span>{language === "it" ? "da" : "by"}: {lead.created_by_name}</span>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LeadsManagement = ({ onLeadUpdate }) => {
  const { language } = useLanguage();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterSector, setFilterSector] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSales, setFilterSales] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [notesDialog, setNotesDialog] = useState(null);
  const [notesText, setNotesText] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [detailLead, setDetailLead] = useState(null); // {lead}
  const [detailEntries, setDetailEntries] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [assignLead, setAssignLead] = useState(null); // lead object being assigned/reassigned
  const [assigningLeadId, setAssigningLeadId] = useState(null);

  const assignLeadTo = async (leadId, salesId, leadName) => {
    setAssigningLeadId(leadId);
    try {
      await axios.patch(`${API}/leads/${leadId}/status`, {
        assigned_to: salesId || null,
      }, { withCredentials: true });
      const sales = salesUsers.find(su => su.user_id === salesId);
      toast.success(
        !salesId
          ? (language === "it" ? `Lead "${leadName}" non assegnato` : `Lead "${leadName}" unassigned`)
          : (language === "it" ? `Lead "${leadName}" assegnato a ${sales?.name || "—"}` : `Lead "${leadName}" assigned to ${sales?.name || "—"}`)
      );
      setAssignLead(null);
      fetchLeads();
      if (onLeadUpdate) onLeadUpdate();
    } catch {
      toast.error(language === "it" ? "Errore durante l'assegnazione" : "Assignment error");
    } finally {
      setAssigningLeadId(null);
    }
  };
  const [sectors, setSectors] = useState(SECTOR_LABELS);
  const [addingSector, setAddingSector] = useState(false);
  const [newSectorName, setNewSectorName] = useState("");
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", email: "", phone: "", sector: "altro", notes: "" });
  const [addingLead, setAddingLead] = useState(false);
  const [salesUsers, setSalesUsers] = useState([]);

  useEffect(() => { fetchLeads(); fetchSectors(); }, []);

  const fetchSectors = async () => {
    try {
      const res = await axios.get(`${API}/sectors`, { withCredentials: true });
      if (res.data.sectors) setSectors(res.data.sectors);
    } catch {}
  };

  const addSector = async () => {
    const name = newSectorName.trim();
    if (!name || name.length < 2) return;
    try {
      const res = await axios.post(`${API}/sectors`, { name }, { withCredentials: true });
      if (res.data.success) {
        toast.success(language === "it" ? `Settore "${name}" aggiunto` : `Sector "${name}" added`);
        setNewSectorName("");
        setAddingSector(false);
        fetchSectors();
      }
    } catch {
      toast.error(language === "it" ? "Errore" : "Error");
    }
  };

  const submitNewLead = async () => {
    if (!newLead.name.trim() || !newLead.phone.trim()) return;
    setAddingLead(true);
    try {
      await axios.post(`${API}/leads/submit`, {
        name: newLead.name, email: newLead.email, phone: newLead.phone,
        sector: newLead.sector, notes: newLead.notes, source_page: "super_admin_manual",
      });
      toast.success(language === "it" ? "Lead aggiunta" : "Lead added");
      setAddLeadOpen(false);
      setNewLead({ name: "", email: "", phone: "", sector: "altro", notes: "" });
      fetchLeads();
      if (onLeadUpdate) onLeadUpdate();
    } catch { toast.error(language === "it" ? "Errore" : "Error"); }
    finally { setAddingLead(false); }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const [response, ass] = await Promise.all([
        axios.get(`${API}/leads/list`, { withCredentials: true }),
        axios.get(`${API}/sales/admin/assignees`, { withCredentials: true }).catch(() => ({ data: { assignees: null } })),
      ]);
      setLeads(response.data.leads);
      setStats(response.data.stats);
      // Use full assignee list (sales + super_admin) when available, fall back to /leads/list
      setSalesUsers(ass.data?.assignees || response.data.sales_users || []);
    } catch {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (leadId, newStatus) => {
    if (newStatus === "delete") {
      if (!window.confirm(language === "it" ? "Sei sicuro di voler eliminare questa lead?" : "Are you sure you want to delete this lead?")) return;
      try {
        await axios.delete(`${API}/leads/${leadId}`, { withCredentials: true });
        setLeads(prev => prev.filter(l => l.lead_id !== leadId));
        toast.success(language === "it" ? "Lead eliminata" : "Lead deleted");
        if (onLeadUpdate) onLeadUpdate();
      } catch {
        toast.error(language === "it" ? "Errore nell'eliminazione" : "Delete error");
      }
      return;
    }
    try {
      await axios.patch(`${API}/leads/${leadId}/status`, {
        status: newStatus,
        contacted: newStatus !== "new"
      }, { withCredentials: true });
      setLeads(prev => prev.map(l => l.lead_id === leadId ? { ...l, status: newStatus, contacted: newStatus !== "new" } : l));
      toast.success(language === "it" ? "Status aggiornato" : "Status updated");
      if (onLeadUpdate) onLeadUpdate();
    } catch {
      toast.error(language === "it" ? "Errore" : "Error");
    }
  };

  const saveNotes = async () => {
    if (!notesDialog) return;
    setSavingNotes(true);
    try {
      await axios.patch(`${API}/leads/${notesDialog.lead_id}/status`, {
        notes: notesText
      }, { withCredentials: true });
      setLeads(prev => prev.map(l => l.lead_id === notesDialog.lead_id ? { ...l, notes: notesText } : l));
      setNotesDialog(null);
      toast.success(language === "it" ? "Note salvate" : "Notes saved");
    } catch {
      toast.error(language === "it" ? "Errore" : "Error");
    } finally {
      setSavingNotes(false);
    }
  };

  const filtered = leads.filter(l => {
    if (filterSector !== "all" && l.sector !== filterSector) return false;
    if (filterStatus !== "all" && l.status !== filterStatus) return false;
    if (filterSales !== "all") {
      if (filterSales === "_unassigned" && l.assigned_to) return false;
      if (filterSales !== "_unassigned" && l.assigned_to !== filterSales) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return l.name?.toLowerCase().includes(q) || l.phone?.includes(q);
    }
    return true;
  });

  const getStatusInfo = (status) => LEAD_STATUSES.find(s => s.value === status) || LEAD_STATUSES[0];

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      return format(new Date(iso), "dd/MM/yy HH:mm");
    } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leads-management">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">{language === "it" ? "Totale Lead" : "Total Leads"}</p>
            <p className="text-3xl font-bold text-blue-700 dark:text-blue-300 mt-1" data-testid="leads-stat-total">{stats.total}</p>
          </div>
          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">{language === "it" ? "Nuovi" : "New"}</p>
            <p className="text-3xl font-bold text-amber-700 dark:text-amber-300 mt-1" data-testid="leads-stat-new">{stats.new}</p>
          </div>
          <div className="p-4 rounded-xl bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-800">
            <p className="text-sm text-violet-600 dark:text-violet-400 font-medium">{language === "it" ? "Contattati" : "Contacted"}</p>
            <p className="text-3xl font-bold text-violet-700 dark:text-violet-300 mt-1">{stats.contacted}</p>
          </div>
          <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
            <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">{language === "it" ? "Per settore" : "By sector"}</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {Object.entries(stats.by_sector || {}).map(([sec, count]) => (
                <span key={sec} className="text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full">
                  {(sectors[sec] || {})[language] || sec}: {count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center" data-testid="leads-filters">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={language === "it" ? "Cerca nome o telefono..." : "Search name or phone..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 rounded-full"
            data-testid="leads-search"
          />
        </div>
        <Select value={filterSector} onValueChange={setFilterSector}>
          <SelectTrigger className="w-[160px] rounded-full" data-testid="leads-filter-sector">
            <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder={language === "it" ? "Settore" : "Sector"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{language === "it" ? "Tutti i settori" : "All sectors"}</SelectItem>
            {Object.entries(sectors).map(([key, labels]) => (
              <SelectItem key={key} value={key}>{labels[language] || labels.it || key}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {addingSector ? (
          <div className="flex items-center gap-1.5">
            <Input
              value={newSectorName}
              onChange={(e) => setNewSectorName(e.target.value)}
              placeholder={language === "it" ? "Nome settore..." : "Sector name..."}
              className="w-[160px] h-9 rounded-full text-sm"
              onKeyDown={(e) => e.key === "Enter" && addSector()}
              autoFocus
              data-testid="new-sector-input"
            />
            <Button size="sm" className="h-9 w-9 p-0 rounded-full" onClick={addSector} disabled={!newSectorName.trim()} data-testid="new-sector-confirm">
              <Check className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" className="h-9 w-9 p-0 rounded-full" onClick={() => { setAddingSector(false); setNewSectorName(""); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={() => setAddingSector(true)} data-testid="add-sector-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />
            {language === "it" ? "Nuovo settore" : "New sector"}
          </Button>
        )}
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px] rounded-full" data-testid="leads-filter-status">
            <CircleDot className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{language === "it" ? "Tutti gli status" : "All statuses"}</SelectItem>
            {LEAD_STATUSES.map(s => (
              <SelectItem key={s.value} value={s.value}>{language === "it" ? s.label_it : s.label_en}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterSales} onValueChange={setFilterSales}>
          <SelectTrigger className="w-[180px] rounded-full" data-testid="leads-filter-sales">
            <Users className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Sales" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{language === "it" ? "Tutti gli owner" : "All owners"}</SelectItem>
            <SelectItem value="_unassigned">{language === "it" ? "Non assegnati" : "Unassigned"}</SelectItem>
            {salesUsers.map(s => (
              <SelectItem key={s.user_id} value={s.user_id}>
                {s.role === "super_admin" ? "👑 " : ""}{s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{filtered.length} {language === "it" ? "risultati" : "results"}</span>
        <div className="flex items-center gap-2 ml-auto">
          <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => {
            import("xlsx").then(XLSX => {
              const statusMap = { new: "Nuovi Lead", contacted: "Contattati", interested: "Interessati", demo_booked: "Demo Fissata", trial: "Trial / Chiusura", closed_won: "Chiuso ✓", recall_later: "Da risentire più avanti", no_reply: "Mai Risposto", not_interested: "Non Interessato" };
              const rows = filtered.map(l => ({
                "Nome": l.name || "",
                "Telefono": l.phone || "",
                "Settore": (sectors[l.sector] || {})[language] || (sectors[l.sector] || {}).it || l.sector || "",
                "Source": l.source_page || "",
                "Data": l.created_at ? new Date(l.created_at).toLocaleDateString("it-IT") : "",
                "Status": statusMap[l.status] || l.status || "",
                "Note": l.notes || "",
              }));
              const ws = XLSX.utils.json_to_sheet(rows);
              ws["!cols"] = [{ wch: 22 }, { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 12 }, { wch: 16 }, { wch: 30 }];
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, "Lead");
              XLSX.writeFile(wb, `SmartBooker_Lead_${new Date().toISOString().slice(0,10)}.xlsx`);
              toast.success(language === "it" ? "Excel esportato" : "Excel exported");
            });
          }} data-testid="export-leads-btn">
            <Download className="w-3.5 h-3.5 mr-1" />
            Excel
          </Button>
          <Button size="sm" className="rounded-full" onClick={() => setAddLeadOpen(true)} data-testid="add-lead-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />
            {language === "it" ? "Aggiungi Lead" : "Add Lead"}
          </Button>
        </div>
      </div>

      {/* Add Lead Dialog */}
      {addLeadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setAddLeadOpen(false)}>
          <div className="bg-card rounded-2xl border shadow-xl p-6 w-full max-w-md mx-4 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="add-lead-dialog">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{language === "it" ? "Nuova Lead" : "New Lead"}</h3>
              <button onClick={() => setAddLeadOpen(false)} className="p-1 rounded-full hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">{language === "it" ? "Nome *" : "Name *"}</label>
                <Input value={newLead.name} onChange={(e) => setNewLead(p => ({...p, name: e.target.value}))} placeholder="Mario Rossi" data-testid="add-lead-name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <Input value={newLead.email} onChange={(e) => setNewLead(p => ({...p, email: e.target.value}))} placeholder="email@esempio.it" data-testid="add-lead-email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{language === "it" ? "Telefono *" : "Phone *"}</label>
                <Input value={newLead.phone} onChange={(e) => setNewLead(p => ({...p, phone: e.target.value}))} placeholder="+39 333 1234567" data-testid="add-lead-phone" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{language === "it" ? "Settore" : "Sector"}</label>
                <Select value={newLead.sector} onValueChange={(v) => setNewLead(p => ({...p, sector: v}))}>
                  <SelectTrigger className="rounded-lg" data-testid="add-lead-sector"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(sectors).map(([k, v]) => <SelectItem key={k} value={k}>{v[language] || v.it || k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Note</label>
                <Input value={newLead.notes} onChange={(e) => setNewLead(p => ({...p, notes: e.target.value}))} placeholder={language === "it" ? "Note opzionali..." : "Optional notes..."} data-testid="add-lead-notes" />
              </div>
            </div>
            <Button className="w-full rounded-lg" onClick={submitNewLead} disabled={!newLead.name.trim() || !newLead.phone.trim() || addingLead} data-testid="add-lead-submit">
              {addingLead ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {language === "it" ? "Aggiungi Lead" : "Add Lead"}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="leads-table">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left py-3 px-4 text-sm font-medium">{language === "it" ? "Nome" : "Name"}</th>
                <th className="text-left py-3 px-4 text-sm font-medium hide-mobile">{language === "it" ? "Telefono" : "Phone"}</th>
                <th className="text-left py-3 px-4 text-sm font-medium hide-mobile">{language === "it" ? "Settore" : "Sector"}</th>
                <th className="text-left py-3 px-4 text-sm font-medium hide-mobile">Source</th>
                <th className="text-left py-3 px-4 text-sm font-medium hide-mobile">{language === "it" ? "Creato da" : "Created by"}</th>
                <th className="text-left py-3 px-4 text-sm font-medium hide-mobile">{language === "it" ? "Owner" : "Owner"}</th>
                <th className="text-left py-3 px-4 text-sm font-medium hide-mobile">{language === "it" ? "Inserito" : "Inserted"}</th>
                <th className="text-left py-3 px-4 text-sm font-medium hide-mobile">{language === "it" ? "Ultima mod." : "Last upd."}</th>
                <th className="text-left py-3 px-4 text-sm font-medium">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium">{language === "it" ? "Azioni" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-muted-foreground">
                    {language === "it" ? "Nessun lead trovato" : "No leads found"}
                  </td>
                </tr>
              ) : filtered.map(lead => {
                const statusInfo = getStatusInfo(lead.status);
                return (
                  <tr
                    key={lead.lead_id}
                    className="border-t hover:bg-violet-50/60 dark:hover:bg-violet-950/20 transition-colors cursor-pointer"
                    data-testid={`lead-row-${lead.lead_id}`}
                    onClick={async (e) => {
                      const tag = (e.target.tagName || "").toUpperCase();
                      if (["A", "BUTTON", "INPUT", "SELECT", "TEXTAREA", "SVG", "PATH"].includes(tag)) return;
                      if (e.target.closest && e.target.closest("a,button,input,select,textarea,[role='combobox']")) return;
                      setDetailLead(lead);
                      setDetailEntries([]);
                      setDetailLoading(true);
                      try {
                        const res = await axios.get(`${API}/leads/${lead.lead_id}/entries`, { withCredentials: true });
                        setDetailEntries(res.data.entries || []);
                        if (res.data.lead) setDetailLead(res.data.lead);
                      } catch {}
                      finally { setDetailLoading(false); }
                    }}
                  >
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-sm">{lead.name}</p>
                        <p className="text-xs text-muted-foreground md:hidden">{lead.phone}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 hide-mobile">
                      <a href={`tel:${lead.phone}`} className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5" />
                        {lead.phone}
                      </a>
                    </td>
                    <td className="py-3 px-4 hide-mobile">
                      <span className="text-sm">{(sectors[lead.sector] || {})[language] || lead.sector}</span>
                    </td>
                    <td className="py-3 px-4 hide-mobile">
                      <span className="text-xs text-muted-foreground font-mono">{lead.source_page || "-"}</span>
                    </td>
                    <td className="py-3 px-4 hide-mobile" data-testid={`lead-created-by-${lead.lead_id}`}>
                      <span className="text-xs">{lead.created_by_name || (lead.source_page && lead.source_page !== "super_admin_manual" ? <em className="text-muted-foreground">{language === "it" ? "Sistema" : "System"}</em> : <span className="text-muted-foreground">—</span>)}</span>
                    </td>
                    <td className="py-3 px-4 hide-mobile" data-testid={`lead-owner-${lead.lead_id}`}>
                      {lead.assigned_sales_name ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 font-medium">
                          {lead.assigned_sales_name}
                        </span>
                      ) : (
                        <span className="text-xs text-amber-600 dark:text-amber-400 italic">{language === "it" ? "Non assegnato" : "Unassigned"}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 hide-mobile">
                      <span className="text-xs text-muted-foreground">{formatDate(lead.created_at)}</span>
                    </td>
                    <td className="py-3 px-4 hide-mobile" data-testid={`lead-updated-${lead.lead_id}`}>
                      <span className="text-xs text-muted-foreground">{lead.updated_at ? formatDate(lead.updated_at) : "—"}</span>
                    </td>
                    <td className="py-3 px-4">
                      <Select value={lead.status || "new"} onValueChange={(v) => updateStatus(lead.lead_id, v)}>
                        <SelectTrigger className={`w-[140px] h-8 rounded-full text-xs font-semibold border-0 ${statusInfo.color}`} data-testid={`lead-status-${lead.lead_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_STATUSES.map(s => (
                            <SelectItem key={s.value} value={s.value}>{language === "it" ? s.label_it : s.label_en}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <a href={`https://wa.me/${lead.phone?.replace(/[^0-9+]/g, '')}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-full" title="WhatsApp">
                            <MessageSquare className="w-4 h-4 text-emerald-500" />
                          </Button>
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 rounded-full"
                          title={language === "it" ? "Note" : "Notes"}
                          onClick={(e) => { e.stopPropagation(); setNotesDialog(lead); setNotesText(lead.notes || ""); }}
                          data-testid={`lead-notes-btn-${lead.lead_id}`}
                        >
                          <FileText className={`w-4 h-4 ${lead.notes ? "text-violet-500" : "text-muted-foreground"}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-8 rounded-full px-2 text-[11px] font-semibold ${
                            lead.assigned_to
                              ? "text-violet-600 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/40"
                              : "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700/60 hover:bg-amber-100"
                          }`}
                          title={lead.assigned_to ? (language === "it" ? "Riassegna" : "Reassign") : (language === "it" ? "Assegna a un sales" : "Assign to a sales")}
                          onClick={(e) => { e.stopPropagation(); setAssignLead(lead); }}
                          disabled={assigningLeadId === lead.lead_id}
                          data-testid={`lead-assign-btn-${lead.lead_id}`}
                        >
                          {assigningLeadId === lead.lead_id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : (
                              <>
                                <UserIcon className="w-3.5 h-3.5 mr-1" />
                                <span className="hidden md:inline">
                                  {lead.assigned_to
                                    ? (lead.assigned_sales_name || (language === "it" ? "Riassegna" : "Reassign"))
                                    : (language === "it" ? "Assegna" : "Assign")}
                                </span>
                              </>
                            )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Lead Dialog */}
      <Dialog open={!!assignLead} onOpenChange={(open) => !open && setAssignLead(null)}>
        <DialogContent className="max-w-md" data-testid="assign-lead-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserIcon className="w-5 h-5 text-violet-500" />
              {language === "it" ? "Assegna lead" : "Assign lead"} — {assignLead?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{assignLead?.phone}</span>
              {assignLead?.assigned_sales_name && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 text-xs font-semibold">
                  {language === "it" ? "Attualmente" : "Currently"}: {assignLead.assigned_sales_name}
                </span>
              )}
            </div>

            <p className="text-xs text-muted-foreground pt-1">
              {language === "it"
                ? "Seleziona un commerciale. Riceverà una notifica nella sua dashboard."
                : "Select a sales user. They'll get a notification in their dashboard."}
            </p>

            <div className="max-h-[300px] overflow-y-auto space-y-1.5 pr-1">
              {salesUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {language === "it" ? "Nessun sales disponibile" : "No sales users available"}
                </p>
              ) : (
                salesUsers.map(su => {
                  const isActive = assignLead?.assigned_to === su.user_id;
                  const isAdmin = su.role === "super_admin";
                  return (
                    <button
                      key={su.user_id}
                      onClick={() => assignLeadTo(assignLead.lead_id, su.user_id, assignLead.name)}
                      disabled={isActive || assigningLeadId === assignLead?.lead_id}
                      className={`w-full text-left p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                        isActive
                          ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 cursor-default"
                          : isAdmin
                            ? "border-amber-200 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-950/10 hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                            : "border-slate-200 dark:border-slate-800 hover:border-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/20"
                      }`}
                      data-testid={`assign-to-${su.user_id}`}
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow ${
                        isAdmin
                          ? "bg-gradient-to-br from-amber-500 to-orange-600"
                          : "bg-gradient-to-br from-violet-500 to-purple-600"
                      }`}>
                        {isAdmin ? <Crown className="w-4 h-4" /> : (su.name || "S").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate flex items-center gap-1.5">
                          {su.name}
                          {isAdmin && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300">
                              {language === "it" ? "ADMIN" : "ADMIN"}
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {su.email}
                          {typeof su.lead_stats?.total_assigned === "number" && (
                            <span className="ml-2">· {su.lead_stats.total_assigned} lead</span>
                          )}
                        </p>
                      </div>
                      {isActive && (
                        <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {assignLead?.assigned_to && (
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => assignLeadTo(assignLead.lead_id, null, assignLead.name)}
                  disabled={assigningLeadId === assignLead?.lead_id}
                  className="w-full rounded-full border-rose-300 text-rose-600 hover:bg-rose-50"
                  data-testid="assign-unassign-btn"
                >
                  {language === "it" ? "Rimuovi assegnazione" : "Unassign"}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={!!notesDialog} onOpenChange={(open) => !open && setNotesDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-500" />
              {language === "it" ? "Note" : "Notes"} — {notesDialog?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{notesDialog?.phone}</span>
              <span>{(sectors[notesDialog?.sector] || {})[language] || notesDialog?.sector}</span>
            </div>
            {notesDialog?.message && (
              <div className="p-3 rounded-lg bg-muted/50 text-sm">
                <p className="text-xs text-muted-foreground mb-1">{language === "it" ? "Messaggio dal form:" : "Form message:"}</p>
                {notesDialog.message}
              </div>
            )}
            <textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              placeholder={language === "it" ? "Aggiungi note interne..." : "Add internal notes..."}
              rows={4}
              className="w-full rounded-lg border p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/50"
              data-testid="lead-notes-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setNotesDialog(null)} className="rounded-full">
                {language === "it" ? "Annulla" : "Cancel"}
              </Button>
              <Button onClick={saveNotes} disabled={savingNotes} className="rounded-full" data-testid="lead-notes-save">
                {savingNotes ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                {language === "it" ? "Salva" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog (read-only timeline + info) */}
      <LeadDetailDrawer
        lead={detailLead}
        entries={detailEntries}
        loading={detailLoading}
        sectors={sectors}
        language={language}
        onClose={() => { setDetailLead(null); setDetailEntries([]); }}
        onEntriesRefresh={async () => {
          if (!detailLead?.lead_id) return;
          setDetailLoading(true);
          try {
            const res = await axios.get(`${API}/leads/${detailLead.lead_id}/entries`, { withCredentials: true });
            setDetailEntries(res.data.entries || []);
            if (res.data.lead) setDetailLead(res.data.lead);
          } catch {}
          finally { setDetailLoading(false); }
        }}
      />
    </div>
  );
};

// ============================================================
// GDPR DASHBOARD
// ============================================================
const REQUEST_STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  processing: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};
const REQUEST_TYPE_LABELS = { access: "Accesso Dati", deletion: "Cancellazione", revoke_marketing: "Revoca Marketing" };
const STATUS_LABELS = { pending: "In attesa", processing: "In lavorazione", completed: "Completata", rejected: "Rifiutata" };

const GDPRDashboard = () => {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("requests");
  const [searchEmail, setSearchEmail] = useState("");
  const [exportingEmail, setExportingEmail] = useState("");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/gdpr/admin/dashboard`, { withCredentials: true });
      setData(res.data);
    } catch { toast.error("Errore caricamento GDPR"); }
    finally { setLoading(false); }
  };

  const updateRequestStatus = async (requestId, status) => {
    try {
      await axios.patch(`${API}/gdpr/admin/request/${requestId}`, { status }, { withCredentials: true });
      toast.success(`Status aggiornato: ${STATUS_LABELS[status]}`);
      fetchData();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const exportData = async (email) => {
    if (!email) return;
    setExportingEmail(email);
    try {
      const res = await axios.get(`${API}/gdpr/admin/export/${encodeURIComponent(email)}`, {
        withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `gdpr_export_${email.replace("@", "_")}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Dati esportati");
    } catch { toast.error("Errore esportazione"); }
    finally { setExportingEmail(""); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const stats = data?.stats || {};

  return (
    <div className="space-y-6" data-testid="gdpr-dashboard">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-4 rounded-xl bg-card border" data-testid="gdpr-stat-consents">
          <p className="text-xs text-muted-foreground">Consensi Totali</p>
          <p className="text-2xl font-bold mt-1">{stats.total_consents || 0}</p>
          <p className="text-xs text-emerald-500 mt-1">Marketing: {stats.marketing_granted || 0} attivi</p>
        </div>
        <div className="p-4 rounded-xl bg-card border" data-testid="gdpr-stat-pending">
          <p className="text-xs text-muted-foreground">Richieste In Attesa</p>
          <p className="text-2xl font-bold mt-1 text-amber-500">{stats.pending_requests || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Da elaborare</p>
        </div>
        <div className="p-4 rounded-xl bg-card border">
          <p className="text-xs text-muted-foreground">In Lavorazione</p>
          <p className="text-2xl font-bold mt-1 text-blue-500">{stats.processing_requests || 0}</p>
        </div>
        <div className="p-4 rounded-xl bg-card border">
          <p className="text-xs text-muted-foreground">Completate</p>
          <p className="text-2xl font-bold mt-1 text-emerald-500">{stats.completed_requests || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">su {stats.total_requests || 0} totali</p>
        </div>
      </div>

      {/* Export tool */}
      <div className="p-4 rounded-xl bg-card border flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="gdpr-export-tool">
        <div className="flex-1">
          <p className="text-sm font-medium">Esporta dati utente</p>
          <p className="text-xs text-muted-foreground">Inserisci email per esportare tutti i dati associati (GDPR Art. 15)</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Input
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            placeholder="email@esempio.it"
            className="w-full sm:w-[220px] h-9 rounded-lg text-sm"
            data-testid="gdpr-export-email"
          />
          <Button
            size="sm"
            onClick={() => exportData(searchEmail)}
            disabled={!searchEmail.includes("@") || exportingEmail}
            className="h-9 rounded-lg"
            data-testid="gdpr-export-btn"
          >
            {exportingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
            Esporta
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 bg-muted rounded-full p-1 w-fit">
        {[
          { key: "requests", label: "Richieste Privacy", count: stats.pending_requests },
          { key: "consents", label: "Registro Consensi" },
          { key: "audit", label: "Audit Trail" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
              tab === t.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`gdpr-tab-${t.key}`}
          >
            {t.label}
            {t.count > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Requests tab */}
      {tab === "requests" && (
        <div className="space-y-3" data-testid="gdpr-requests-list">
          {(data?.requests || []).length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">Nessuna richiesta privacy ricevuta</div>
          ) : (
            (data?.requests || []).map((req) => (
              <div key={req.request_id} className="p-4 rounded-xl bg-card border flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid={`gdpr-request-${req.request_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${REQUEST_STATUS_COLORS[req.status]}`}>{STATUS_LABELS[req.status]}</span>
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-medium">{REQUEST_TYPE_LABELS[req.request_type]}</span>
                  </div>
                  <p className="text-sm font-medium mt-1.5">{req.email}</p>
                  {req.notes && <p className="text-xs text-muted-foreground mt-0.5">{req.notes}</p>}
                  <p className="text-[10px] text-muted-foreground mt-1">{new Date(req.created_at).toLocaleString("it-IT")}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {req.status === "pending" && (
                    <>
                      <Button size="sm" variant="outline" className="h-8 text-xs rounded-lg" onClick={() => updateRequestStatus(req.request_id, "processing")} data-testid={`gdpr-process-${req.request_id}`}>
                        In lavorazione
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs rounded-lg text-red-500 border-red-200" onClick={() => updateRequestStatus(req.request_id, "rejected")}>
                        Rifiuta
                      </Button>
                    </>
                  )}
                  {req.status === "processing" && (
                    <>
                      {req.request_type === "access" && (
                        <Button size="sm" variant="outline" className="h-8 text-xs rounded-lg" onClick={() => exportData(req.email)}>
                          <Download className="w-3 h-3 mr-1" /> Esporta
                        </Button>
                      )}
                      <Button size="sm" className="h-8 text-xs rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white" onClick={() => updateRequestStatus(req.request_id, "completed")} data-testid={`gdpr-complete-${req.request_id}`}>
                        <Check className="w-3 h-3 mr-1" /> Completa
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Consents tab */}
      {tab === "consents" && (
        <div className="space-y-2" data-testid="gdpr-consents-list">
          {(data?.consents || []).length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">Nessun consenso registrato</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="pb-2 pr-4">Email</th>
                    <th className="pb-2 pr-4">Tipo</th>
                    <th className="pb-2 pr-4">Stato</th>
                    <th className="pb-2 pr-4">Origine</th>
                    <th className="pb-2">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.consents || []).slice(0, 50).map((c, i) => (
                    <tr key={i} className="border-b border-muted/30">
                      <td className="py-2 pr-4 font-medium">{c.email}</td>
                      <td className="py-2 pr-4 capitalize">{c.consent_type}</td>
                      <td className="py-2 pr-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${c.granted ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"}`}>
                          {c.granted ? "Concesso" : "Revocato"}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground text-xs">{c.source}</td>
                      <td className="py-2 text-muted-foreground text-xs">{new Date(c.created_at).toLocaleString("it-IT")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Audit tab */}
      {tab === "audit" && (
        <div className="space-y-1.5" data-testid="gdpr-audit-list">
          {(data?.audit || []).length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">Nessuna attivita registrata</div>
          ) : (
            (data?.audit || []).slice(0, 100).map((a, i) => (
              <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-muted/30 transition-colors text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="font-medium">{a.action}</span>
                  <span className="text-muted-foreground"> — {a.email}</span>
                  {a.details && <p className="text-muted-foreground mt-0.5 truncate">{a.details}</p>}
                  <p className="text-[10px] text-muted-foreground/60 mt-0.5">{new Date(a.created_at).toLocaleString("it-IT")}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SuperAdmin;
